import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { GlobalFileSearchPanel } from "@/components/features/files/GlobalFileSearchPanel";
import {
  canDownloadFile,
  canOfferFileDuplication,
} from "@/lib/data/requests/files/file-adapter";
import { MassTortMattersFolder } from "@/components/features/files/MassTortMattersFolder";
import { VirtualMatterFolderList } from "@/components/features/files/VirtualMatterFolderList";
import { FileVersionTitle } from "@/components/features/files/FileVersionTitle";
import { downloadFileLink } from "@/lib/utils/matterFile";
import {
  downloadFile,
  retrieveFile,
  updateFile,
  fileDestination,
  fileOperationMessage,
} from "@/lib/data/requests/files/canonical-files";
import { invalidateFileQueries } from "@/lib/data/react-query/files/canonical-files";
import { EmptyPreviewState } from "./EmptyPreviewState";
import { AddOrEditFileModal } from "@/components/features/matter/file-repo/AddOrEditFileModal";
import { CreateDocxFileSlideover } from "@/components/features/matter/file-repo/CreateDocxFileSlideover";
import { CreateDocxSlideover } from "@/components/features/matter/file-repo/CreateDocxSlideover";
import { EditDocxFileSlideover } from "@/components/features/matter/file-repo/EditDocxFileSlideover";
import { type RepoItem } from "@/components/features/matter/file-repo/FileRepository";
import { FileRepositoryPdfEditorSlideover } from "@/components/features/matter/file-repo/FileRepositoryPdfEditorSlideover";
import { FileViewer } from "@/components/features/matter/file-repo/FileViewer";
import { GlobalFileContextMenu } from "@/components/features/files/GlobalFileContextMenu";
import { useFileDuplication } from "@/components/features/files/duplication/FileDuplicationProvider";
import { usePersonalFiles } from "@/components/features/file-library/usePersonalFiles";
import { MY_FILES_UI_ENABLED } from "@/components/features/file-library/visibility";
import {
  FolderTree,
  TREE_ICON_CLASS,
  TREE_ROW_CLASS,
  TREE_TEXT_CLASS,
} from "@/components/features/matter/file-repo/FolderTree";
import { AddOrEditTemplateModal } from "@/components/features/settings/defaults/matter-templates/AddOrEditTemplateModal";
import { BulkUploadMatterTemplatesModal } from "@/components/features/settings/defaults/matter-templates/BulkUploadMatterTemplatesModal";
import { DuplicateTemplateModal } from "@/components/features/settings/defaults/matter-templates/DuplicateTemplateModal";
import { EditDocxTemplateModal } from "@/components/features/settings/defaults/matter-templates/EditDocxTemplateModal";
import { AddOrEditFirmFileModal } from "@/components/features/settings/firm/AddOrEditFirmFileModal";
import { CreateDocxFirmFileModal } from "@/components/features/settings/firm/CreateDocxFirmFileModal";
import { EditDocxFirmFileModal } from "@/components/features/settings/firm/EditDocxFirmFileModal";
import { EditPdfFirmFileModal } from "@/components/features/settings/firm/EditPdfFirmFileModal";
import {
  FIRM_FILE_TREE_ITEM_TYPE,
  FirmFolderTree,
} from "@/components/features/settings/firm/FirmFolderTree";
import {
  ActionConfirmationModal,
  AppPageShell,
  Button,
  Card,
  Input,
  LoadingSpinner,
  MatterPreviewHoverCard,
  type MatterPreviewData,
  Skeleton,
  Text,
} from "@/components/ui";
import { RepoUploadSplitButton } from "@/components/ui/RepoUploadSplitButton/RepoUploadSplitButton";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { useUser } from "@/components/UserProvider";
import { useDeleteFirmFile, useUpdateFirmFile } from "@/lib/data/react-query";
import {
  retrieveFirmFilesOptions,
  useCreateFirmFile,
} from "@/lib/data/react-query/firm/firm-file";
import {
  retrieveMatterFilesQueryOptions,
  useCreateMatterFile,
  useDeleteMatterFile,
  useUpdateMatterFile,
} from "@/lib/data/react-query/matters/matter-file";
import { searchAllMattersQueryOptions } from "@/lib/data/react-query/matters/search-matters";
import {
  retrieveMatterTemplatesQueryOptions,
  useCreateMatterTemplate,
  useDeleteMatterTemplate,
} from "@/lib/data/react-query/matters/matter-template";
import { retrieveMassTortSearchQueryOptions } from "@/lib/data/react-query/mass-torts/mass-tort";
import { MATTER_STATUS_CATEGORY_OPTIONS } from "@/lib/data/static/matterStatics";
import { type SearchMatterStatusCategory } from "@/lib/data/requests/matters/search-matters";
import { generateS3ItemPreSignedUrlV1 } from "@/lib/data/requests/s3";
import {
  PAGE_PERMISSION_KEYS,
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { FirmFile, MatterFile, MatterTemplate } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import {
  getFirmFileDisplayName,
  getFirmFileDisplayNamesInFolder,
  getFirmFileExtension,
  isFirmFolderDescendant,
} from "@/lib/utils/firmFile";
import {
  getFileIcon,
  getFileNameParts,
  getFileDocumentFormat,
  buildUniqueFileName,
  handleDownload,
} from "@/lib/utils/matterFile";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronRight,
  CopyPlus,
  Download,
  ExternalLink,
  File as FileIcon,
  FilesIcon,
  FileText,
  Folder,
  FolderOpen,
  FolderKanban,
  Pencil,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDrop } from "react-dnd";
import toast from "react-hot-toast";

type MatterFilesSection =
  | "matters"
  | "intake"
  | "referred-matters"
  | "declined-matters";
type FilesSection =
  | "personal"
  | "firm"
  | "templates"
  | "mass-torts"
  | MatterFilesSection;

type MatterEditorState = {
  open: boolean;
  editing: boolean;
  activeFileId?: string;
  isFolder: boolean;
  autoOpenUpload?: boolean;
};

type FirmEditorState = {
  open: boolean;
  editing: boolean;
  activeFile?: FirmFile;
  isFolder: boolean;
  autoOpenUpload?: boolean;
};

type TemplateEditorState = {
  open: boolean;
  editing: boolean;
  activeTemplate?: MatterTemplate;
  autoOpenUpload?: boolean;
};

type MatterDeleteState = {
  item: MatterFile;
  matterId: string;
};

type MassTortDeleteState = {
  item: MatterFile;
  massTortId: string;
};

type MassTortFolderListItem = {
  massTortId: string;
  massTortTitle: string;
};

type MassTortFilesViewData = {
  massTortId: string;
  massTortTitle: string;
  matterFile: MatterFile[];
};

type RepoDragPayload = {
  items: RepoItem[];
};

type MatterFilesViewData = {
  matterId: string;
  matterTitle: string;
  matterFile: MatterFile[];
};
type MatterFolderListItem = Omit<MatterPreviewData, "matterId" | "category"> & {
  matterId: string;
  massTortId?: string | null;
  category: SearchMatterStatusCategory | null;
};

const FILES_SECTION_ORDER: FilesSection[] = [
  "personal",
  "firm",
  "templates",
  "mass-torts",
  "matters",
  "intake",
  "referred-matters",
  "declined-matters",
];
const MATTER_PREVIEW_LOADING_MIN_MS = 300;
const GLOBAL_FILES_TEMPLATE_DRAG_TYPE = "GLOBAL_FILES_TEMPLATE_ITEM";

const getFirstExpandedSection = (sections: Set<FilesSection>) =>
  FILES_SECTION_ORDER.find((section) => sections.has(section)) ?? null;

const areSectionSetsEqual = (
  first: Set<FilesSection>,
  second: Set<FilesSection>,
) =>
  first.size === second.size &&
  Array.from(first).every((section) => second.has(section));

const isRepoDragPayload = (payload: unknown): payload is RepoDragPayload =>
  typeof payload === "object" &&
  payload !== null &&
  "items" in payload &&
  Array.isArray((payload as RepoDragPayload).items);

const isFirmFileDragPayload = (
  payload: unknown,
): payload is { file: FirmFile } =>
  typeof payload === "object" &&
  payload !== null &&
  "file" in payload &&
  Boolean((payload as { file?: FirmFile }).file);

const updatePendingItemIds = (
  current: Set<string>,
  itemIds: string[],
  isPending: boolean,
) => {
  const next = new Set(current);

  itemIds.forEach((itemId) => {
    if (isPending) {
      next.add(itemId);
      return;
    }

    next.delete(itemId);
  });

  return next;
};

const countFirmDescendantFiles = (items: FirmFile[], folderId: string) => {
  const ids = new Set<string>();
  const stack = [folderId];

  while (stack.length > 0) {
    const currentId = stack.pop();
    if (!currentId) continue;

    items.forEach((item) => {
      if ((item.idLocation || "") !== currentId) return;
      if (item.isFolder) {
        stack.push(item.id);
      } else {
        ids.add(item.id);
      }
    });
  }

  return ids.size;
};

const countMatterDescendantFiles = (items: RepoItem[], folderId: string) => {
  const ids = new Set<string>();
  const stack = [folderId];

  while (stack.length > 0) {
    const currentId = stack.pop();
    if (currentId === undefined) continue;

    items.forEach((item) => {
      if ((item.idLocation || "") !== currentId) return;
      if (item.isFolder) {
        stack.push(item.id);
      } else {
        ids.add(item.id);
      }
    });
  }

  return ids.size;
};

const ExplorerSkeleton = ({ rows = 8 }: { rows?: number }) => {
  const widths = ["55%", "72%", "48%", "64%", "58%", "68%", "52%", "61%"];

  return (
    <div className="py-2">
      {Array.from({ length: rows }).map((_, index) => (
        <div
          key={`explorer-skeleton-${index}`}
          className="flex items-center gap-2 px-2 py-2"
        >
          <Skeleton className={cn(TREE_ICON_CLASS, "rounded-sm")} />
          <Skeleton
            className="h-5 rounded-sm"
            style={{ width: widths[index % widths.length] }}
          />
        </div>
      ))}
    </div>
  );
};

const PreviewSkeleton = () => {
  return (
    <div className="space-y-4 p-6">
      <Skeleton className="h-8 w-48 rounded-lg" />
      <Skeleton className="h-4 w-32 rounded-lg" />
      <Skeleton className="h-[420px] w-full rounded-2xl" />
    </div>
  );
};

const ExplorerInlineLoading = ({ label }: { label: string }) => {
  return (
    <div className="flex items-center gap-2 px-2 py-2">
      <LoadingSpinner className={TREE_ICON_CLASS} />
      <Text variant="subHeading" as="span">
        {label}
      </Text>
    </div>
  );
};

const EXPLORER_ROW_CLASS = cn(
  TREE_ROW_CLASS,
  "w-full cursor-pointer text-left",
);
const EXPLORER_ROW_SELECTED_CLASS =
  "bg-primary/10 text-primary hover:bg-primary/20";
const EXPLORER_ROW_LABEL_CLASS = TREE_TEXT_CLASS;
const PREVIEW_TITLE_CLASS =
  "mb-0 block min-w-0 flex-1 truncate text-nowrap break-normal";
const SYSTEM_FOLDER_ICON_CLASS = "text-sky-400";
const USER_FOLDER_ICON_CLASS = "text-amber-500";
const MATTER_FILES_SECTION_TITLES: Record<MatterFilesSection, string> = {
  matters: "Matters",
  intake: "Intake",
  "referred-matters": "Referred matters",
  "declined-matters": "Declined matters",
};

const isMatterFilesSection = (
  section: FilesSection | null,
): section is MatterFilesSection =>
  section === "matters" ||
  section === "intake" ||
  section === "referred-matters" ||
  section === "declined-matters";

const hasExpandedMatterFilesSection = (sections: Set<FilesSection>) =>
  Array.from(sections).some(isMatterFilesSection);

const MATTER_STATUS_CATEGORIES_BY_SECTION: Record<
  MatterFilesSection,
  readonly string[]
> = {
  matters: ["pre-litigation", "litigation", "closed"],
  intake: ["opportunity", "lead"],
  "referred-matters": ["referred"],
  "declined-matters": ["declined"],
};

const getMatterSectionForCategory = (
  category: MatterFolderListItem["category"],
): MatterFilesSection => {
  if (category === "opportunity" || category === "lead") {
    return "intake";
  }

  if (category === "referred") {
    return "referred-matters";
  }

  if (category === "declined") {
    return "declined-matters";
  }

  return "matters";
};

export function GlobalFilesView() {
  const { hasPagePermission, hasActionPermission } = useUser();
  const { openDuplication } = useFileDuplication();

  const canViewFirmFiles = hasPagePermission(PAGE_PERMISSION_KEYS.filesAccess);
  const canCreateFirmFiles = hasActionPermission(
    ACTION_PERMISSION_KEYS.firmFile,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdateFirmFiles = hasActionPermission(
    ACTION_PERMISSION_KEYS.firmFile,
    ACTION_PERMISSION_VALUES.update,
  );
  const canEditFirmDocuments =
    canUpdateFirmFiles &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.firmFile,
      ACTION_PERMISSION_VALUES.update,
    );
  const canDeleteFirmFiles = hasActionPermission(
    ACTION_PERMISSION_KEYS.firmFile,
    ACTION_PERMISSION_VALUES.delete,
  );

  const canViewTemplates = canViewFirmFiles;
  const canCreateTemplates = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTemplate,
    ACTION_PERMISSION_VALUES.create,
  );
  const canDeleteTemplates = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTemplate,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canUpdateTemplates = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTemplate,
    ACTION_PERMISSION_VALUES.update,
  );
  const canEditTemplateDocuments =
    canUpdateTemplates &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterTemplate,
      ACTION_PERMISSION_VALUES.update,
    );

  const canViewMatterFiles = canViewFirmFiles;
  const canCreateMatterFiles = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterFile,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdateMatterFiles = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterFile,
    ACTION_PERMISSION_VALUES.update,
  );
  const canEditMatterDocuments =
    canUpdateMatterFiles &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterFile,
      ACTION_PERMISSION_VALUES.update,
    );
  const canDeleteMatterFiles = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterFile,
    ACTION_PERMISSION_VALUES.delete,
  );

  const canViewMassTortFiles = canViewFirmFiles;

  const [activeSection, setActiveSection] = useState<FilesSection | null>(null);
  const [explorerScrollElement, setExplorerScrollElement] =
    useState<HTMLDivElement | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<FilesSection>>(
    new Set(),
  );
  const personalFiles = usePersonalFiles({
    enabled: MY_FILES_UI_ENABLED && expandedSections.has("personal"),
    onSelect: () => {
      setActiveSection("personal");
    },
  });

  useEffect(() => {
    const allowedSections = new Set<FilesSection>();
    if (MY_FILES_UI_ENABLED) allowedSections.add("personal");

    if (canViewFirmFiles) {
      allowedSections.add("firm");
    }
    if (canViewTemplates) {
      allowedSections.add("templates");
    }
    if (canViewMatterFiles) {
      allowedSections.add("matters");
      allowedSections.add("intake");
      allowedSections.add("referred-matters");
      allowedSections.add("declined-matters");
    }
    if (canViewMassTortFiles) {
      allowedSections.add("mass-torts");
    }

    if (allowedSections.size === 0) {
      setExpandedSections(new Set());
      setActiveSection(null);
      return;
    }

    const nextExpandedSections = new Set(
      Array.from(expandedSections).filter((section) =>
        allowedSections.has(section),
      ),
    );

    if (!areSectionSetsEqual(expandedSections, nextExpandedSections)) {
      setExpandedSections(nextExpandedSections);
    }
    setActiveSection((prev) =>
      prev && nextExpandedSections.has(prev)
        ? prev
        : getFirstExpandedSection(nextExpandedSections),
    );
  }, [
    canViewFirmFiles,
    canViewMassTortFiles,
    canViewMatterFiles,
    canViewTemplates,
    expandedSections,
  ]);

  const [search, setSearch] = useState("");
  const isSearching = Boolean(search.trim());
  const [versionControlsContainer, setVersionControlsContainer] =
    useState<HTMLDivElement | null>(null);

  const [firmCurrentFolderId, setFirmCurrentFolderId] = useState("");
  const [firmSelectedFileId, setFirmSelectedFileId] = useState<string | null>(
    null,
  );
  const firmMultiFileInputRef = useRef<HTMLInputElement>(null);
  const [firmEditorState, setFirmEditorState] = useState<FirmEditorState>({
    open: false,
    editing: false,
    isFolder: false,
  });
  const [firmItemToDelete, setFirmItemToDelete] = useState<FirmFile>();
  const [firmDocxEditorFile, setFirmDocxEditorFile] = useState<FirmFile | null>(
    null,
  );
  const [isCreateFirmDocxOpen, setIsCreateFirmDocxOpen] = useState(false);
  const [firmPdfEditorFile, setFirmPdfEditorFile] = useState<FirmFile | null>(
    null,
  );
  const [firmMovingItemIds, setFirmMovingItemIds] = useState<Set<string>>(
    new Set(),
  );

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [templateEditorState, setTemplateEditorState] =
    useState<TemplateEditorState>({
      open: false,
      editing: false,
    });
  const [isCreateTemplateDocxOpen, setIsCreateTemplateDocxOpen] =
    useState(false);
  const [isBulkUploadTemplatesOpen, setIsBulkUploadTemplatesOpen] =
    useState(false);
  const [templateDocxEditorTemplate, setTemplateDocxEditorTemplate] =
    useState<MatterTemplate | null>(null);
  const [templateMovingItemIds, setTemplateMovingItemIds] = useState<
    Set<string>
  >(new Set());
  const [templateToDelete, setTemplateToDelete] =
    useState<MatterTemplate | null>(null);
  const [templateToDuplicate, setTemplateToDuplicate] =
    useState<MatterTemplate | null>(null);

  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(null);
  const [expandedMatterIds, setExpandedMatterIds] = useState<Set<string>>(
    new Set(),
  );
  const [isClosedMatterFolderExpanded, setIsClosedMatterFolderExpanded] =
    useState(false);
  const [matterCurrentFolderIds, setMatterCurrentFolderIds] = useState<
    Record<string, string>
  >({});
  const matterMultiFileInputRef = useRef<HTMLInputElement>(null);
  const [matterSelectedItemIds, setMatterSelectedItemIds] = useState<
    Record<string, string | null>
  >({});
  const [isMatterPreviewLoading, setIsMatterPreviewLoading] = useState(false);
  const [matterPreviewLoadingItemId, setMatterPreviewLoadingItemId] = useState<
    string | null
  >(null);
  const matterPreviewLoadingStartedAtRef = useRef<number | null>(null);
  const matterPreviewLoadingTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [matterEditorState, setMatterEditorState] = useState<MatterEditorState>(
    {
      open: false,
      editing: false,
      isFolder: false,
    },
  );
  const [matterDocxEditorFile, setMatterDocxEditorFile] =
    useState<MatterFile | null>(null);
  const [isCreateMatterDocxOpen, setIsCreateMatterDocxOpen] = useState(false);
  const [matterPdfEditorFile, setMatterPdfEditorFile] =
    useState<MatterFile | null>(null);
  const [matterMovingItemIds, setMatterMovingItemIds] = useState<Set<string>>(
    new Set(),
  );
  const [matterItemToDelete, setMatterItemToDelete] =
    useState<MatterDeleteState | null>(null);

  const [selectedMassTortId, setSelectedMassTortId] = useState<string | null>(
    null,
  );
  const [expandedMassTortIds, setExpandedMassTortIds] = useState<Set<string>>(
    new Set(),
  );
  const [expandedMassTortMattersIds, setExpandedMassTortMattersIds] = useState<
    Set<string>
  >(new Set());
  const [activeMassTortMattersId, setActiveMassTortMattersId] = useState<
    string | null
  >(null);
  const isMassTortMattersActive =
    activeSection === "mass-torts" &&
    activeMassTortMattersId === selectedMassTortId &&
    activeMassTortMattersId !== null;
  const isMatterRepositoryActive =
    isMatterFilesSection(activeSection) ||
    (isMassTortMattersActive && selectedMatterId !== null);
  const [massTortCurrentFolderIds, setMassTortCurrentFolderIds] = useState<
    Record<string, string>
  >({});
  const massTortMultiFileInputRef = useRef<HTMLInputElement>(null);
  const [massTortSelectedItemIds, setMassTortSelectedItemIds] = useState<
    Record<string, string | null>
  >({});
  const [massTortEditorState, setMassTortEditorState] =
    useState<MatterEditorState>({
      open: false,
      editing: false,
      isFolder: false,
    });
  const [massTortMovingItemIds, setMassTortMovingItemIds] = useState<
    Set<string>
  >(new Set());
  const [massTortItemToDelete, setMassTortItemToDelete] =
    useState<MassTortDeleteState | null>(null);
  const massTortCurrentFolderId = selectedMassTortId
    ? (massTortCurrentFolderIds[selectedMassTortId] ?? "")
    : "";
  const massTortSelectedItemId = selectedMassTortId
    ? (massTortSelectedItemIds[selectedMassTortId] ?? null)
    : null;
  const matterCurrentFolderId = selectedMatterId
    ? (matterCurrentFolderIds[selectedMatterId] ?? "")
    : "";
  const matterSelectedItemId = selectedMatterId
    ? (matterSelectedItemIds[selectedMatterId] ?? null)
    : null;

  const {
    data: firmFilesResponse,
    isLoading: isFirmFilesLoading,
    refetch: refetchFirmFiles,
  } = useQuery(
    retrieveFirmFilesOptions(expandedSections.has("firm") && canViewFirmFiles),
  );
  const firmFiles = useMemo(
    () => firmFilesResponse?.data ?? [],
    [firmFilesResponse?.data],
  );

  const {
    data: templatesResponse,
    isLoading: isTemplatesLoading,
    isFetching: isTemplatesFetching,
  } = useQuery({
    ...retrieveMatterTemplatesQueryOptions(),
    enabled: expandedSections.has("templates") && canViewTemplates,
  });
  const templates = useMemo(
    () => templatesResponse?.data ?? [],
    [templatesResponse?.data],
  );
  const isTemplatesPending =
    isTemplatesLoading ||
    (isTemplatesFetching && templatesResponse === undefined);
  const expandedMatterStatusCategories = useMemo(
    () =>
      Array.from(
        new Set(
          Array.from(expandedSections)
            .filter(isMatterFilesSection)
            .flatMap((section) => MATTER_STATUS_CATEGORIES_BY_SECTION[section]),
        ),
      ),
    [expandedSections],
  );

  const {
    data: mattersResponse,
    isLoading: isMatterFoldersLoading,
    isFetching: isMatterFoldersFetching,
  } = useQuery({
    ...searchAllMattersQueryOptions({
      context: "files",
      pageSize: 500,
      sort: "title",
      order: "asc",
      statusCategories:
        expandedMatterStatusCategories as SearchMatterStatusCategory[],
    }),
    enabled: expandedMatterStatusCategories.length > 0 && canViewMatterFiles,
  });
  const expandedMassTortMattersIdList = useMemo(
    () => Array.from(expandedMassTortMattersIds),
    [expandedMassTortMattersIds],
  );
  const massTortMattersQueries = useQueries({
    queries: expandedMassTortMattersIdList.map((massTortId) => ({
      ...searchAllMattersQueryOptions({
        context: "files",
        massTortId,
        statusCategories: [...MATTER_STATUS_CATEGORY_OPTIONS],
        pageSize: 500,
        sort: "title",
        order: "asc",
      }),
      enabled:
        expandedSections.has("mass-torts") &&
        expandedMassTortIds.has(massTortId) &&
        canViewMatterFiles,
    })),
  });
  const allMatterResults = useMemo(
    () =>
      Array.from(
        new Map(
          [
            ...(mattersResponse?.data ?? []),
            ...massTortMattersQueries.flatMap(
              (query) => query.data?.data ?? [],
            ),
          ].map((matter) => [matter.id, matter]),
        ).values(),
      ),
    [mattersResponse?.data, massTortMattersQueries],
  );
  const matterFolders = useMemo(
    () =>
      [...allMatterResults]
        .map(
          (matter): MatterFolderListItem => ({
            matterId: matter.id,
            massTortId: matter.massTortId,
            matterAssignee:
              matter.assignees
                ?.map((assignee) =>
                  `${assignee.user.firstName} ${assignee.user.lastName}`.trim(),
                )
                .filter(Boolean) ?? [],
            matterClientName:
              getMatterContactConnections(matter)
                .filter((connection) => connection.label === "Client")
                .map(
                  (connection) =>
                    connection.contact.title ??
                    connection.contact.organizationName ??
                    connection.contact.groupName ??
                    [
                      connection.contact.individualFirstName,
                      connection.contact.individualLastName,
                    ]
                      .filter(Boolean)
                      .join(" "),
                )
                .find(Boolean) ?? null,
            matterDateOfLoss: matter.dateOfLoss,
            matterDefendantName: getMatterContactConnections(matter)
              .filter((connection) => connection.label === "Defendant")
              .map(
                (connection) =>
                  connection.contact.title ??
                  connection.contact.organizationName ??
                  connection.contact.groupName ??
                  [
                    connection.contact.individualFirstName,
                    connection.contact.individualLastName,
                  ]
                    .filter(Boolean)
                    .join(" "),
              )
              .filter(Boolean),
            matterIncidentAddressCity: matter.incidentAddressCity,
            matterIncidentAddressState: matter.incidentAddressState,
            label: matter.status?.label,
            matterTag: [],
            matterTeam: [],
            matterTitle: matter.title,
            category: (matter.status?.categoryId ??
              null) as SearchMatterStatusCategory | null,
            incidentType: matter.incidentType?.label,
          }),
        )
        .sort((a, b) => {
          const leftTitle =
            typeof a.matterTitle === "string" ? a.matterTitle : "";
          const rightTitle =
            typeof b.matterTitle === "string" ? b.matterTitle : "";

          return leftTitle.localeCompare(rightTitle);
        }),
    [allMatterResults],
  );
  const matterFoldersById = useMemo(
    () =>
      new Map(
        matterFolders.map((matter) => [matter.matterId, matter] as const),
      ),
    [matterFolders],
  );
  const openMatterFolders = useMemo(
    () =>
      matterFolders.filter(
        (matter) =>
          !matter.massTortId &&
          (matter.category === "pre-litigation" ||
            matter.category === "litigation"),
      ),
    [matterFolders],
  );
  const closedMatterFolders = useMemo(
    () =>
      matterFolders.filter(
        (matter) => !matter.massTortId && matter.category === "closed",
      ),
    [matterFolders],
  );
  const intakeMatterFolders = useMemo(
    () =>
      matterFolders.filter(
        (matter) => !matter.massTortId && matter.category === "lead",
      ),
    [matterFolders],
  );
  const referredMatterFolders = useMemo(
    () =>
      matterFolders.filter(
        (matter) => !matter.massTortId && matter.category === "referred",
      ),
    [matterFolders],
  );
  const declinedMatterFolders = useMemo(
    () =>
      matterFolders.filter(
        (matter) => !matter.massTortId && matter.category === "declined",
      ),
    [matterFolders],
  );
  const matterFolderTitleById = useMemo(
    () =>
      new Map(
        matterFolders.map(
          (matter) => [matter.matterId, matter.matterTitle] as const,
        ),
      ),
    [matterFolders],
  );

  const {
    data: massTortSearchResponse,
    isLoading: isMassTortFoldersLoading,
    isFetching: isMassTortFoldersFetching,
  } = useQuery({
    ...retrieveMassTortSearchQueryOptions(),
    enabled: expandedSections.has("mass-torts") && canViewMassTortFiles,
  });
  const massTortFolders = useMemo(
    () =>
      [...(massTortSearchResponse?.data ?? [])]
        .map(
          (massTort): MassTortFolderListItem => ({
            massTortId: massTort.id,
            massTortTitle: massTort.title?.trim() || "Untitled mass tort",
          }),
        )
        .sort((a, b) => a.massTortTitle.localeCompare(b.massTortTitle)),
    [massTortSearchResponse?.data],
  );
  const massTortFolderTitleById = useMemo(
    () =>
      new Map(
        massTortFolders.map(
          (massTort) => [massTort.massTortId, massTort.massTortTitle] as const,
        ),
      ),
    [massTortFolders],
  );

  const expandedMatterIdList = useMemo(
    () => Array.from(expandedMatterIds),
    [expandedMatterIds],
  );
  const expandedMatterQueries = useQueries({
    queries: expandedMatterIdList.map((matterId) => ({
      ...retrieveMatterFilesQueryOptions(matterId),
      enabled:
        (hasExpandedMatterFilesSection(expandedSections) ||
          expandedSections.has("mass-torts")) &&
        canViewMatterFiles,
    })),
  });
  const expandedMatterMap = useMemo(() => {
    const map = new Map<string, MatterFilesViewData>();

    expandedMatterIdList.forEach((matterId, index) => {
      const matterFiles = expandedMatterQueries[index]?.data?.data;
      if (matterFiles && Array.isArray(matterFiles)) {
        map.set(matterId, {
          matterId,
          matterTitle: matterFolderTitleById.get(matterId) || "",
          matterFile: matterFiles,
        });
      }
    });

    return map;
  }, [expandedMatterIdList, expandedMatterQueries, matterFolderTitleById]);

  const expandedMassTortIdList = useMemo(
    () => Array.from(expandedMassTortIds),
    [expandedMassTortIds],
  );
  const expandedMassTortQueries = useQueries({
    queries: expandedMassTortIdList.map((massTortId) => ({
      ...retrieveMatterFilesQueryOptions({ massTortId }),
      enabled: expandedSections.has("mass-torts") && canViewMassTortFiles,
    })),
  });
  const expandedMassTortMap = useMemo(() => {
    const map = new Map<string, MassTortFilesViewData>();

    expandedMassTortIdList.forEach((massTortId, index) => {
      const massTortFiles = expandedMassTortQueries[index]?.data?.data;
      if (massTortFiles && Array.isArray(massTortFiles)) {
        map.set(massTortId, {
          massTortId,
          massTortTitle: massTortFolderTitleById.get(massTortId) || "",
          matterFile: massTortFiles,
        });
      }
    });

    return map;
  }, [
    expandedMassTortIdList,
    expandedMassTortQueries,
    massTortFolderTitleById,
  ]);
  const expandedMassTortLoadingMap = useMemo(() => {
    const map = new Map<string, boolean>();

    expandedMassTortIdList.forEach((massTortId, index) => {
      const query = expandedMassTortQueries[index];
      const massTortData = query.data?.data;
      const hasMassTortData = Boolean(
        massTortData && Array.isArray(massTortData),
      );
      map.set(
        massTortId,
        Boolean(query.isLoading || (query.isFetching && !hasMassTortData)),
      );
    });

    return map;
  }, [expandedMassTortIdList, expandedMassTortQueries]);
  const selectedMassTortQuery = useMemo(() => {
    if (!selectedMassTortId) {
      return undefined;
    }

    const selectedMassTortIndex =
      expandedMassTortIdList.indexOf(selectedMassTortId);
    if (selectedMassTortIndex === -1) {
      return undefined;
    }

    return expandedMassTortQueries[selectedMassTortIndex];
  }, [expandedMassTortIdList, expandedMassTortQueries, selectedMassTortId]);
  const selectedMassTort = selectedMassTortId
    ? expandedMassTortMap.get(selectedMassTortId)
    : undefined;
  const isSelectedMassTortLoading = Boolean(
    selectedMassTortQuery?.isLoading ||
      (selectedMassTortQuery?.isFetching && !selectedMassTort),
  );
  const selectedMassTortFolder = useMemo(
    () =>
      massTortFolders.find(
        (massTort) => massTort.massTortId === selectedMassTortId,
      ),
    [massTortFolders, selectedMassTortId],
  );

  const expandedMatterLoadingMap = useMemo(() => {
    const map = new Map<string, boolean>();

    expandedMatterIdList.forEach((matterId, index) => {
      const query = expandedMatterQueries[index];
      const matterData = query.data?.data;
      const hasMatterData = Boolean(matterData && Array.isArray(matterData));
      map.set(
        matterId,
        Boolean(query.isLoading || (query.isFetching && !hasMatterData)),
      );
    });

    return map;
  }, [expandedMatterIdList, expandedMatterQueries]);
  const selectedMatterQuery = useMemo(() => {
    if (!selectedMatterId) {
      return undefined;
    }

    const selectedMatterIndex = expandedMatterIdList.indexOf(selectedMatterId);
    if (selectedMatterIndex === -1) {
      return undefined;
    }

    return expandedMatterQueries[selectedMatterIndex];
  }, [expandedMatterIdList, expandedMatterQueries, selectedMatterId]);
  const selectedMatter = selectedMatterId
    ? expandedMatterMap.get(selectedMatterId)
    : undefined;
  const isSelectedMatterLoading = Boolean(
    selectedMatterQuery?.isLoading ||
      (selectedMatterQuery?.isFetching && !selectedMatter),
  );
  const selectedMatterFolder = useMemo(
    () => matterFolders.find((matter) => matter.matterId === selectedMatterId),
    [matterFolders, selectedMatterId],
  );
  const isSelectedMatterClosed =
    !selectedMatterFolder?.massTortId &&
    selectedMatterFolder?.category === "closed";
  const activeMatterSection = isMatterFilesSection(activeSection)
    ? activeSection
    : "matters";
  const activeMatterSectionTitle =
    MATTER_FILES_SECTION_TITLES[activeMatterSection];
  const getFirstExpandedMassTortId = useCallback(
    (massTortIds: Set<string>) =>
      massTortFolders.find((massTort) => massTortIds.has(massTort.massTortId))
        ?.massTortId ?? null,
    [massTortFolders],
  );
  const getFirstExpandedMatterId = useCallback(
    (matterIds: Set<string>) =>
      matterFolders.find(
        (matter) => !matter.massTortId && matterIds.has(matter.matterId),
      )?.matterId ?? null,
    [matterFolders],
  );
  const setMatterCurrentFolderIdForMatter = useCallback(
    (matterId: string, folderId: string) => {
      setMatterCurrentFolderIds((prev) => ({
        ...prev,
        [matterId]: folderId,
      }));
    },
    [],
  );
  const setMatterSelectedItemIdForMatter = useCallback(
    (matterId: string, itemId: string | null) => {
      setMatterSelectedItemIds((prev) => ({
        ...prev,
        [matterId]: itemId,
      }));
    },
    [],
  );
  const setMassTortCurrentFolderIdForMassTort = useCallback(
    (massTortId: string, folderId: string) => {
      setMassTortCurrentFolderIds((prev) => ({
        ...prev,
        [massTortId]: folderId,
      }));
    },
    [],
  );
  const setMassTortSelectedItemIdForMassTort = useCallback(
    (massTortId: string, itemId: string | null) => {
      setMassTortSelectedItemIds((prev) => ({
        ...prev,
        [massTortId]: itemId,
      }));
    },
    [],
  );
  const setFirmItemsMoving = useCallback(
    (itemIds: string[], isMoving: boolean) => {
      setFirmMovingItemIds((prev) =>
        updatePendingItemIds(prev, itemIds, isMoving),
      );
    },
    [],
  );
  const setTemplateItemsMoving = useCallback(
    (itemIds: string[], isMoving: boolean) => {
      setTemplateMovingItemIds((prev) =>
        updatePendingItemIds(prev, itemIds, isMoving),
      );
    },
    [],
  );
  const setMatterItemsMoving = useCallback(
    (itemIds: string[], isMoving: boolean) => {
      setMatterMovingItemIds((prev) =>
        updatePendingItemIds(prev, itemIds, isMoving),
      );
    },
    [],
  );
  const setMassTortItemsMoving = useCallback(
    (itemIds: string[], isMoving: boolean) => {
      setMassTortMovingItemIds((prev) =>
        updatePendingItemIds(prev, itemIds, isMoving),
      );
    },
    [],
  );

  const moveCreateFirmFile = useCreateFirmFile({ showToast: false });
  const moveCreateMatterTemplate = useCreateMatterTemplate({
    showToast: false,
  });
  const moveDeleteMatterTemplate = useDeleteMatterTemplate({
    showToast: false,
  });
  const createMatterTemplate = useCreateMatterTemplate();
  const moveDeleteFirmFile = useDeleteFirmFile({ showToast: false });
  const fileQueryClient = useQueryClient();

  const createFirmFile = useCreateFirmFile();
  const createMatterFile = useCreateMatterFile();
  const deleteFirmFile = useDeleteFirmFile();
  const updateFirmFile = useUpdateFirmFile();
  const deleteMatterFile = useDeleteMatterFile();
  const updateMatterFile = useUpdateMatterFile();
  const deleteMatterTemplate = useDeleteMatterTemplate();

  const orderedTemplates = useMemo(
    () => [...templates].sort((a, b) => a.name.localeCompare(b.name)),
    [templates],
  );
  const templatesById = useMemo(
    () =>
      new Map(templates.map((template) => [template.id, template] as const)),
    [templates],
  );

  const getMatterRepoItems = useCallback(
    (matter?: MatterFilesViewData) =>
      (matter?.matterFile ?? []).map((item) => ({
        ...item,
        kind: "regular" as const,
      })),
    [],
  );
  const selectedMatterRepoItems = useMemo<RepoItem[]>(
    () => getMatterRepoItems(selectedMatter),
    [getMatterRepoItems, selectedMatter],
  );
  const getMassTortRepoItems = useCallback(
    (massTort?: MassTortFilesViewData) =>
      (massTort?.matterFile ?? []).map((item) => ({
        ...item,
        kind: "regular" as const,
      })),
    [],
  );
  const selectedMassTortRepoItems = useMemo<RepoItem[]>(
    () => getMassTortRepoItems(selectedMassTort),
    [getMassTortRepoItems, selectedMassTort],
  );
  const templateRepoItems = useMemo<RepoItem[]>(
    () =>
      orderedTemplates.map(
        (template) =>
          ({
            id: template.id,
            name: template.name,
            idLocation: "",
            isFolder: false,
            kind: "regular",
          }) as RepoItem,
      ),
    [orderedTemplates],
  );
  const firmCurrentFolder = useMemo(
    () =>
      firmFiles.find(
        (file) => file.id === firmCurrentFolderId && file.isFolder,
      ),
    [firmCurrentFolderId, firmFiles],
  );
  const firmSelectedFile = useMemo(
    () =>
      firmFiles.find(
        (file) => file.id === firmSelectedFileId && !file.isFolder,
      ),
    [firmFiles, firmSelectedFileId],
  );
  const firmSelectedItem = firmSelectedFile ?? firmCurrentFolder;
  const selectedFirmFileExtension = firmSelectedFile
    ? getFileDocumentFormat({
        ...firmSelectedFile,
        name: getFirmFileDisplayName(firmSelectedFile),
      })
    : "";
  const isSelectedFirmDocx = selectedFirmFileExtension === "docx";
  const isSelectedFirmPdf = selectedFirmFileExtension === "pdf";
  const canEditSelectedFirmDocx =
    Boolean(firmSelectedFile) && canEditFirmDocuments && isSelectedFirmDocx;
  const canEditSelectedFirmPdf =
    Boolean(firmSelectedFile) && canEditFirmDocuments && isSelectedFirmPdf;

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId),
    [selectedTemplateId, templates],
  );
  const selectedTemplateFileExtension = selectedTemplate
    ? getFileNameParts(
        selectedTemplate.name || selectedTemplate.presignedUrlKey || "",
      ).fileExtension.toLowerCase()
    : "";
  const isSelectedTemplateDocx = selectedTemplateFileExtension === "docx";
  const canEditSelectedTemplateDocx =
    Boolean(selectedTemplate) &&
    canEditTemplateDocuments &&
    isSelectedTemplateDocx;

  const selectedMatterItem = useMemo(
    () =>
      selectedMatterRepoItems.find((item) => item.id === matterSelectedItemId),
    [matterSelectedItemId, selectedMatterRepoItems],
  );

  const selectedMatterFile = useMemo(
    () =>
      selectedMatterRepoItems.find(
        (item) => item.id === matterSelectedItemId && !item.isFolder,
      ),
    [matterSelectedItemId, selectedMatterRepoItems],
  );
  const selectedMatterFileExtension = selectedMatterFile
    ? getFileDocumentFormat(selectedMatterFile)
    : "";
  const isSelectedMatterDocx = selectedMatterFileExtension === "docx";
  const isSelectedMatterPdf = selectedMatterFileExtension === "pdf";
  const canEditSelectedMatterDocx =
    Boolean(selectedMatterFile) &&
    canEditMatterDocuments &&
    isSelectedMatterDocx;
  const canEditSelectedMatterPdf =
    Boolean(selectedMatterFile) &&
    canEditMatterDocuments &&
    isSelectedMatterPdf;

  const selectedMassTortItem = useMemo(
    () =>
      selectedMassTortRepoItems.find(
        (item) => item.id === massTortSelectedItemId,
      ),
    [massTortSelectedItemId, selectedMassTortRepoItems],
  );
  const selectedMassTortFile = useMemo(
    () =>
      selectedMassTortRepoItems.find(
        (item) => item.id === massTortSelectedItemId && !item.isFolder,
      ),
    [massTortSelectedItemId, selectedMassTortRepoItems],
  );

  const downloadSourceFile = useCallback(
    async ({
      fileName,
      itemType,
      key,
    }: {
      fileName: string;
      itemType: "firm-file" | "matter-template" | "matter-file";
      key: string;
    }): Promise<File> => {
      const presignedUrlResponse = await generateS3ItemPreSignedUrlV1(itemType)(
        {
          clientMethod: "get_object",
          key,
        },
      );

      const response = await fetch(presignedUrlResponse.data.presignedUrl, {
        method: "GET",
      });
      if (!response.ok) {
        throw new Error(`Failed to download source file: ${response.status}`);
      }

      const blob = await response.blob();
      const contentType =
        response.headers.get("content-type") ||
        blob.type ||
        "application/octet-stream";

      const downloadedFile: File = new File([blob], fileName, {
        type: contentType,
      });

      return downloadedFile;
    },
    [],
  );

  const downloadGlobalFile = useCallback(
    async (
      item: Pick<
        MatterFile | MatterTemplate | FirmFile,
        "id" | "name" | "presignedUrlKey"
      >,
      itemType: "firm-file" | "matter-file" | "matter-template",
    ) => {
      if (itemType === "matter-template" && !item.presignedUrlKey) {
        toast.error("This file is not available to download.");
        return;
      }

      try {
        if (itemType !== "matter-template") {
          const result = await downloadFile(item.id);
          if (itemType === "firm-file")
            await handleDownload(
              getFirmFileDisplayName({ ...item, isFolder: false }),
              result.url,
            );
          else downloadFileLink(result.fileName, result.url);
          return;
        }
        const presigned = await generateS3ItemPreSignedUrlV1(itemType)({
          clientMethod: "get_object",
          key: item.presignedUrlKey || "",
        });
        await handleDownload(item.name, presigned.data.presignedUrl);
      } catch (error) {
        console.error("Failed to download file", error);
        toast.error("Unable to download this file. Please try again.");
      }
    },
    [],
  );

  const moveTemplateFilesToFirm = useCallback(
    async (payload: unknown, targetFolderId: string) => {
      if (!canCreateFirmFiles) {
        toast.error("You do not have permission to create firm files.");
        return;
      }
      if (!canDeleteTemplates) {
        toast.error(
          "You do not have permission to remove templates after moving them.",
        );
        return;
      }
      if (!isRepoDragPayload(payload)) {
        return;
      }

      const droppedTemplates = payload.items
        .map((item) => templatesById.get(item.id))
        .filter(Boolean) as MatterTemplate[];

      if (droppedTemplates.length === 0) {
        return;
      }
      const movingTemplateIds = droppedTemplates.map((template) => template.id);

      const reservedFirmNames = firmFiles.map((file) =>
        getFirmFileDisplayName(file),
      );
      let movedCount = 0;

      setTemplateItemsMoving(movingTemplateIds, true);
      try {
        for (const template of droppedTemplates) {
          if (!template.presignedUrlKey) {
            toast.error(`${template.name} is not available to move.`);
            continue;
          }

          const firmFileName = buildUniqueFileName(
            template.name,
            reservedFirmNames,
          );
          reservedFirmNames.push(firmFileName);

          const file = await downloadSourceFile({
            fileName: firmFileName,
            itemType: "matter-template",
            key: template.presignedUrlKey,
          });

          await moveCreateFirmFile.mutateAsync({
            file,
            description: template.description ?? "",
            idLocation: targetFolderId || null,
            isFolder: false,
            name: firmFileName,
          });

          await moveDeleteMatterTemplate.mutateAsync({
            id: template.id,
          });
          movedCount += 1;
        }

        if (movedCount > 0) {
          toast.success(
            movedCount === 1
              ? "Template moved to firm files."
              : `${movedCount} templates moved to firm files.`,
          );
        }
      } catch (error) {
        console.error("Failed to move template file to firm files", error);
        toast.error("Unable to move the template file to firm files.");
      } finally {
        setTemplateItemsMoving(movingTemplateIds, false);
      }
    },
    [
      canCreateFirmFiles,
      canDeleteTemplates,
      moveCreateFirmFile,
      moveDeleteMatterTemplate,
      downloadSourceFile,
      firmFiles,
      setTemplateItemsMoving,
      templatesById,
    ],
  );

  const moveFirmFileToTemplates = useCallback(
    async (firmFile: FirmFile) => {
      if (!canCreateTemplates) {
        toast.error("You do not have permission to create templates.");
        return;
      }
      if (!canDeleteFirmFiles) {
        toast.error(
          "You do not have permission to remove firm files after moving them.",
        );
        return;
      }
      if (firmFile.isFolder) {
        toast.error("Folders cannot be moved into templates.");
        return;
      }
      if (getFirmFileExtension(firmFile).toLowerCase() !== "docx") {
        toast.error("Only DOCX files can be moved into templates.");
        return;
      }
      if (!firmFile.presignedUrlKey) {
        toast.error("This firm file is not available to move.");
        return;
      }

      const reservedTemplateNames = templates.map((template) => template.name);

      setFirmItemsMoving([firmFile.id], true);
      try {
        const name = buildUniqueFileName(
          getFirmFileDisplayName(firmFile),
          reservedTemplateNames,
        );

        const file = await downloadSourceFile({
          fileName: name,
          itemType: "firm-file",
          key: firmFile.presignedUrlKey,
        });

        await moveCreateMatterTemplate.mutateAsync({
          file,
          category: [],
          description: firmFile.description ?? "",
          name,
        });

        await moveDeleteFirmFile.mutateAsync({
          id: firmFile.id,
          isFolder: false,
        });
        toast.success("Firm file moved to templates.");
      } catch (error) {
        console.error("Failed to move firm file to templates", error);
        toast.error("Unable to move the firm file to templates.");
      } finally {
        setFirmItemsMoving([firmFile.id], false);
      }
    },
    [
      canCreateTemplates,
      canDeleteFirmFiles,
      downloadSourceFile,
      moveCreateMatterTemplate,
      moveDeleteFirmFile,
      setFirmItemsMoving,
      templates,
    ],
  );

  const [{ isOver: isOverTemplateDropZone }, templateDropRef] = useDrop(
    () => ({
      accept: FIRM_FILE_TREE_ITEM_TYPE,
      canDrop: (payload: unknown) =>
        canCreateTemplates &&
        isFirmFileDragPayload(payload) &&
        !payload.file.isFolder,
      drop: (payload: unknown, monitor) => {
        if (monitor.didDrop() || !isFirmFileDragPayload(payload)) {
          return;
        }

        if (getFirmFileExtension(payload.file).toLowerCase() !== "docx") {
          toast.error("Only DOCX files can be moved into templates.");
          return;
        }

        void moveFirmFileToTemplates(payload.file);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
      }),
    }),
    [canCreateTemplates, moveFirmFileToTemplates],
  );

  const clearMatterPreviewLoadingTimeout = useCallback(() => {
    if (matterPreviewLoadingTimeoutRef.current) {
      clearTimeout(matterPreviewLoadingTimeoutRef.current);
      matterPreviewLoadingTimeoutRef.current = null;
    }
  }, []);

  const finishMatterPreviewLoading = useCallback(
    (itemId?: string | null) => {
      clearMatterPreviewLoadingTimeout();
      setIsMatterPreviewLoading(false);
      setMatterPreviewLoadingItemId((current) => {
        if (!itemId || current === itemId) {
          return null;
        }
        return current;
      });
      matterPreviewLoadingStartedAtRef.current = null;
    },
    [clearMatterPreviewLoadingTimeout],
  );

  const handleMatterPreviewLoadingChange = useCallback(
    (isLoading: boolean) => {
      const itemId = selectedMatterFile?.id ?? matterSelectedItemId;

      if (!itemId) {
        finishMatterPreviewLoading();
        return;
      }

      clearMatterPreviewLoadingTimeout();

      if (isLoading) {
        if (!matterPreviewLoadingStartedAtRef.current) {
          matterPreviewLoadingStartedAtRef.current = Date.now();
        }
        setIsMatterPreviewLoading(true);
        setMatterPreviewLoadingItemId(itemId);
        return;
      }

      const elapsed = matterPreviewLoadingStartedAtRef.current
        ? Date.now() - matterPreviewLoadingStartedAtRef.current
        : MATTER_PREVIEW_LOADING_MIN_MS;
      const remaining = Math.max(0, MATTER_PREVIEW_LOADING_MIN_MS - elapsed);

      if (remaining === 0) {
        finishMatterPreviewLoading(itemId);
        return;
      }

      matterPreviewLoadingTimeoutRef.current = setTimeout(() => {
        finishMatterPreviewLoading(itemId);
      }, remaining);
    },
    [
      clearMatterPreviewLoadingTimeout,
      finishMatterPreviewLoading,
      matterSelectedItemId,
      selectedMatterFile?.id,
    ],
  );

  useEffect(() => {
    if (!firmCurrentFolderId || firmFiles.length === 0 || isFirmFilesLoading) {
      return;
    }
    if (
      !firmFiles.some(
        (file) => file.id === firmCurrentFolderId && file.isFolder,
      )
    ) {
      setFirmCurrentFolderId("");
    }
  }, [firmCurrentFolderId, firmFiles, isFirmFilesLoading]);

  useEffect(() => {
    if (
      firmSelectedFileId &&
      !firmFiles.some(
        (file) => file.id === firmSelectedFileId && !file.isFolder,
      )
    ) {
      setFirmSelectedFileId(null);
    }
  }, [firmFiles, firmSelectedFileId]);

  useEffect(() => {
    if (
      selectedTemplateId &&
      !templates.some((template) => template.id === selectedTemplateId)
    ) {
      setSelectedTemplateId(null);
    }
  }, [selectedTemplateId, templates]);

  useEffect(() => {
    const validMatterIds = new Set(
      matterFolders.map((matter) => matter.matterId),
    );
    const nextExpandedMatterIds = new Set(
      Array.from(expandedMatterIds).filter((matterId) =>
        validMatterIds.has(matterId),
      ),
    );

    if (
      nextExpandedMatterIds.size !== expandedMatterIds.size ||
      Array.from(nextExpandedMatterIds).some(
        (matterId) => !expandedMatterIds.has(matterId),
      )
    ) {
      setExpandedMatterIds(nextExpandedMatterIds);
    }

    setSelectedMatterId((prev) => {
      if (!prev) return null;
      const selectedFolder = matterFoldersById.get(prev);
      if (
        nextExpandedMatterIds.has(prev) &&
        (!isMassTortMattersActive ||
          selectedFolder?.massTortId === activeMassTortMattersId)
      )
        return prev;
      return isMassTortMattersActive
        ? null
        : getFirstExpandedMatterId(nextExpandedMatterIds);
    });
  }, [
    expandedMatterIds,
    getFirstExpandedMatterId,
    matterFolders,
    matterFoldersById,
    isMassTortMattersActive,
    activeMassTortMattersId,
  ]);

  useEffect(() => {
    const validMassTortIds = new Set(
      massTortFolders.map((massTort) => massTort.massTortId),
    );
    const nextExpandedMassTortIds = new Set(
      Array.from(expandedMassTortIds).filter((massTortId) =>
        validMassTortIds.has(massTortId),
      ),
    );

    if (
      nextExpandedMassTortIds.size !== expandedMassTortIds.size ||
      Array.from(nextExpandedMassTortIds).some(
        (massTortId) => !expandedMassTortIds.has(massTortId),
      )
    ) {
      setExpandedMassTortIds(nextExpandedMassTortIds);
    }

    setSelectedMassTortId((prev) =>
      prev && nextExpandedMassTortIds.has(prev)
        ? prev
        : getFirstExpandedMassTortId(nextExpandedMassTortIds),
    );
  }, [expandedMassTortIds, getFirstExpandedMassTortId, massTortFolders]);

  useEffect(() => {
    if (
      !selectedMatterFolder?.massTortId &&
      selectedMatterFolder?.category === "closed"
    ) {
      setIsClosedMatterFolderExpanded(true);
    }
  }, [selectedMatterFolder?.category, selectedMatterFolder?.massTortId]);

  useEffect(() => {
    if (closedMatterFolders.length === 0 && isClosedMatterFolderExpanded) {
      setIsClosedMatterFolderExpanded(false);
    }
  }, [closedMatterFolders.length, isClosedMatterFolderExpanded]);

  useEffect(() => {
    clearMatterPreviewLoadingTimeout();
    matterPreviewLoadingStartedAtRef.current = null;
    setIsMatterPreviewLoading(false);
    setMatterPreviewLoadingItemId(null);
    if (!selectedMatterId) {
      setMatterDocxEditorFile(null);
      setMatterPdfEditorFile(null);
    }
  }, [clearMatterPreviewLoadingTimeout, selectedMatterId]);

  useEffect(() => {
    return () => {
      clearMatterPreviewLoadingTimeout();
    };
  }, [clearMatterPreviewLoadingTimeout]);

  const expandSection = (section: FilesSection) => {
    const nextExpandedSections = new Set(expandedSections);
    nextExpandedSections.add(section);

    setExpandedSections(nextExpandedSections);
    setActiveSection(section);
  };

  const collapseSection = (section: FilesSection) => {
    const nextExpandedSections = new Set(expandedSections);
    nextExpandedSections.delete(section);

    setExpandedSections(nextExpandedSections);
    setActiveSection((prev) =>
      prev === section ? getFirstExpandedSection(nextExpandedSections) : prev,
    );
  };

  const toggleSection = (section: FilesSection) => {
    if (!expandedSections.has(section)) {
      expandSection(section);
      return;
    }

    if (activeSection !== section) {
      setActiveSection(section);
      return;
    }

    collapseSection(section);
  };

  const selectMatterFolder = (
    matter: MatterFolderListItem,
    massTortId?: string,
  ) => {
    setActiveSection(
      massTortId ? "mass-torts" : getMatterSectionForCategory(matter.category),
    );
    setActiveMassTortMattersId(massTortId ?? null);
    if (massTortId) setSelectedMassTortId(massTortId);
    if (!massTortId && matter.category === "closed") {
      setIsClosedMatterFolderExpanded(true);
    }
    setExpandedMatterIds((prev) => {
      const next = new Set(prev);
      next.add(matter.matterId);
      return next;
    });
    setSelectedMatterId(matter.matterId);
  };

  const collapseMatterFolder = (matterId: string, massTortId?: string) => {
    const nextExpandedMatterIds = new Set(expandedMatterIds);
    nextExpandedMatterIds.delete(matterId);

    setExpandedMatterIds(nextExpandedMatterIds);
    setSelectedMatterId((prev) =>
      prev === matterId
        ? massTortId
          ? null
          : getFirstExpandedMatterId(nextExpandedMatterIds)
        : prev,
    );
  };

  const selectMassTortFolder = (massTort: MassTortFolderListItem) => {
    setActiveSection("mass-torts");
    setActiveMassTortMattersId(null);
    setExpandedMassTortIds((prev) => {
      const next = new Set(prev);
      next.add(massTort.massTortId);
      return next;
    });
    setSelectedMassTortId(massTort.massTortId);
  };

  const collapseMassTortFolder = (massTortId: string) => {
    const nextExpandedMassTortIds = new Set(expandedMassTortIds);
    nextExpandedMassTortIds.delete(massTortId);

    setExpandedMassTortIds(nextExpandedMassTortIds);
    setSelectedMassTortId((prev) =>
      prev === massTortId
        ? getFirstExpandedMassTortId(nextExpandedMassTortIds)
        : prev,
    );
  };

  const toggleClosedMatterFolder = () => {
    setActiveSection("matters");

    if (!closedMatterFolders.length) {
      return;
    }

    if (!isClosedMatterFolderExpanded) {
      setIsClosedMatterFolderExpanded(true);
      return;
    }

    setIsClosedMatterFolderExpanded(false);
    setSelectedMatterId((prev) => {
      if (!prev || matterFoldersById.get(prev)?.category !== "closed") {
        return prev;
      }

      const nextVisibleExpandedMatterIds = new Set(
        Array.from(expandedMatterIds).filter(
          (matterId) => matterFoldersById.get(matterId)?.category !== "closed",
        ),
      );

      return getFirstExpandedMatterId(nextVisibleExpandedMatterIds);
    });
  };

  const moveFirmItem = async (item: FirmFile, targetFolderId: string) => {
    if ((item.idLocation || "") === targetFolderId) {
      return;
    }

    if (item.isFolder) {
      if (item.id === targetFolderId) {
        toast.error("Cannot move a folder into itself.");
        return;
      }

      if (isFirmFolderDescendant(firmFiles, item.id, targetFolderId)) {
        toast.error("Cannot move a folder into one of its descendants.");
        return;
      }
    }

    setFirmItemsMoving([item.id], true);
    try {
      const latestFirmFiles = await refetchFirmFiles();
      const latestItem =
        latestFirmFiles.data?.data.find((file) => file.id === item.id) ?? item;

      await updateFirmFile.mutateAsync({
        id: latestItem.id,
        dateModified: latestItem.dateModified,
        idLocation: targetFolderId || null,
      });
    } catch {
      // Mutation-level error handling already surfaces feedback.
    } finally {
      setFirmItemsMoving([item.id], false);
    }
  };

  const moveMatterItem = (
    item: MatterFile,
    targetFolderId: string,
    matterContext?: MatterFilesViewData,
  ) => {
    const activeMatter = matterContext ?? selectedMatter;
    if (!activeMatter) return false;

    if (item.matterId !== activeMatter.matterId) {
      if (!canCreateMatterFiles) {
        toast.error("You do not have permission to create files.");
        return false;
      }
      if (!canDeleteMatterFiles) {
        toast.error(
          "You do not have permission to remove files after moving them.",
        );
        return false;
      }
      if (item.isFolder) {
        toast.error("Moving folders between matters is not supported.");
        return false;
      }
      if (!item.presignedUrlKey) {
        toast.error("This matter file is not available to move.");
        return false;
      }
      setMatterItemsMoving([item.id], true);
      void (async () => {
        try {
          const current = await retrieveFile(item.id);
          await updateFile(item.id, {
            revision: item.revision ?? current.revision,
            destination: fileDestination(
              "matter",
              targetFolderId,
              activeMatter.matterId,
            ),
          });
          await invalidateFileQueries(fileQueryClient);
          toast.success(
            `File moved to ${activeMatter.matterTitle || "the selected matter"}.`,
          );
        } catch (error) {
          toast.error(
            fileOperationMessage(
              error,
              "Unable to move the file to the selected matter.",
            ),
          );
        } finally {
          setMatterItemsMoving([item.id], false);
        }
      })();

      return true;
    }

    if (!canUpdateMatterFiles) {
      toast.error("You do not have permission to move items.");
      return false;
    }

    if (item.id === targetFolderId) {
      toast.error("Cannot move a folder into itself.");
      return false;
    }

    if ((item.idLocation || "") === targetFolderId) {
      return false;
    }

    if (item.isFolder) {
      let currentFolderId = targetFolderId;

      while (currentFolderId) {
        const folder = activeMatter.matterFile.find(
          (matterFile) => matterFile.id === currentFolderId,
        );

        if (!folder) break;

        if (folder.id === item.id) {
          toast.error("Cannot move a folder into its descendants.");
          return false;
        }

        currentFolderId = folder.idLocation ?? "";
      }
    }

    setMatterItemsMoving([item.id], true);
    void (async () => {
      try {
        await updateMatterFile.mutateAsync({
          matterId: activeMatter.matterId,
          id: item.id,
          idLocation: targetFolderId,
          dateModified: item.dateModified,
        });
      } catch {
        // Mutation-level error handling already surfaces feedback.
      } finally {
        setMatterItemsMoving([item.id], false);
      }
    })();

    return true;
  };

  const moveMassTortItem = (
    item: MatterFile,
    targetFolderId: string,
    massTortContext?: MassTortFilesViewData,
  ) => {
    const activeMassTort = massTortContext ?? selectedMassTort;
    if (!activeMassTort) return false;

    if (!canUpdateMatterFiles) {
      toast.error("You do not have permission to move items.");
      return false;
    }

    if (item.id === targetFolderId) {
      toast.error("Cannot move a folder into itself.");
      return false;
    }

    if ((item.idLocation || "") === targetFolderId) {
      return false;
    }

    if (item.isFolder) {
      let currentFolderId = targetFolderId;

      while (currentFolderId) {
        const folder = activeMassTort.matterFile.find(
          (massTortFile) => massTortFile.id === currentFolderId,
        );

        if (!folder) break;

        if (folder.id === item.id) {
          toast.error("Cannot move a folder into its descendants.");
          return false;
        }

        currentFolderId = folder.idLocation ?? "";
      }
    }

    setMassTortItemsMoving([item.id], true);
    void (async () => {
      try {
        await updateMatterFile.mutateAsync({
          massTortId: activeMassTort.massTortId,
          id: item.id,
          idLocation: targetFolderId,
          dateModified: item.dateModified,
        });
      } catch {
        // Mutation-level error handling already surfaces feedback.
      } finally {
        setMassTortItemsMoving([item.id], false);
      }
    })();

    return true;
  };

  const openFirmCreate = (
    isFolder: boolean,
    options?: { autoOpenUpload?: boolean },
  ) => {
    setFirmEditorState({
      open: true,
      editing: false,
      isFolder,
      autoOpenUpload: options?.autoOpenUpload,
    });
  };

  const openFirmEdit = (item: FirmFile) => {
    setFirmEditorState({
      open: true,
      editing: true,
      activeFile: item,
      isFolder: Boolean(item.isFolder),
    });
  };

  const openTemplateCreate = (options?: { autoOpenUpload?: boolean }) => {
    setTemplateEditorState({
      open: true,
      editing: false,
      autoOpenUpload: options?.autoOpenUpload,
    });
  };

  const openTemplateEdit = (template: MatterTemplate) => {
    setTemplateEditorState({
      open: true,
      editing: true,
      activeTemplate: template,
    });
  };

  const openMatterCreate = (
    isFolder: boolean,
    options?: { autoOpenUpload?: boolean },
  ) => {
    setMatterEditorState({
      open: true,
      editing: false,
      isFolder,
      autoOpenUpload: options?.autoOpenUpload,
    });
  };

  const openMassTortCreate = (
    isFolder: boolean,
    options?: { autoOpenUpload?: boolean },
  ) => {
    setMassTortEditorState({
      open: true,
      editing: false,
      isFolder,
      autoOpenUpload: options?.autoOpenUpload,
    });
  };

  const openMultiFilePicker = useCallback(
    (inputRef: { current: HTMLInputElement | null }) => {
      if (!inputRef.current) return;
      inputRef.current.value = "";
      inputRef.current.click();
    },
    [],
  );

  const handleFirmMultiFileUpload = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;

      Array.from(fileList).forEach((file) => {
        createFirmFile.mutate({
          name: getFileNameParts(file.name).fileName,
          idLocation: firmCurrentFolderId,
          isFolder: false,
          file,
        });
      });
    },
    [createFirmFile, firmCurrentFolderId],
  );

  const handleMatterMultiFileUpload = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0 || !selectedMatterId) return;

      Array.from(fileList).forEach((file) => {
        createMatterFile.mutate({
          matterId: selectedMatterId,
          isFolder: false,
          idLocation: matterCurrentFolderId,
          name: file.name,
          file,
        });
      });
    },
    [createMatterFile, matterCurrentFolderId, selectedMatterId],
  );

  const handleMassTortMultiFileUpload = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0 || !selectedMassTortId) return;

      Array.from(fileList).forEach((file) => {
        createMatterFile.mutate({
          massTortId: selectedMassTortId,
          isFolder: false,
          idLocation: massTortCurrentFolderId,
          name: file.name,
          file,
        });
      });
    },
    [createMatterFile, massTortCurrentFolderId, selectedMassTortId],
  );

  const openMatterEdit = (item: MatterFile) => {
    setMatterEditorState({
      open: true,
      editing: true,
      activeFileId: item.id,
      isFolder: Boolean(item.isFolder),
    });
  };

  const openMassTortEdit = (item: MatterFile) => {
    setMassTortEditorState({
      open: true,
      editing: true,
      activeFileId: item.id,
      isFolder: Boolean(item.isFolder),
    });
  };

  const openFirmDocxEditor = () => {
    if (!firmSelectedFile || !canEditSelectedFirmDocx) return;
    setFirmDocxEditorFile(firmSelectedFile);
  };

  const openFirmPdfEditor = () => {
    if (!firmSelectedFile || !canEditSelectedFirmPdf) return;
    setFirmPdfEditorFile(firmSelectedFile);
  };

  const openTemplateDocxEditor = () => {
    if (!selectedTemplate || !canEditSelectedTemplateDocx) return;
    setTemplateDocxEditorTemplate(selectedTemplate);
  };

  const openMatterDocxEditor = () => {
    if (!selectedMatterFile || !canEditSelectedMatterDocx) return;
    setMatterDocxEditorFile(selectedMatterFile);
  };

  const openMatterPdfEditor = () => {
    if (!selectedMatterFile || !canEditSelectedMatterPdf) return;
    setMatterPdfEditorFile(selectedMatterFile);
  };

  const handleCreateDocxTemplate = useCallback(
    async (file: File) => {
      const fileNameParts = getFileNameParts(file.name);
      const fileExtension = fileNameParts.fileExtension
        ? `.${fileNameParts.fileExtension}`
        : "";
      const normalizedFileName = fileNameParts.fileName
        ? `${fileNameParts.fileName}${fileExtension}`
        : file.name;

      await createMatterTemplate.mutateAsync({
        name: normalizedFileName,
        description: "",
        category: [],
        file,
      });
    },
    [createMatterTemplate],
  );

  const handleDeleteFirmItem = async () => {
    if (!firmItemToDelete) return;

    await deleteFirmFile.mutateAsync({
      id: firmItemToDelete.id,
      isFolder: firmItemToDelete.isFolder,
    });

    if (firmSelectedFileId === firmItemToDelete.id) {
      setFirmSelectedFileId(null);
    }

    setFirmItemToDelete(undefined);
  };

  const handleDeleteMatterItem = async () => {
    if (!matterItemToDelete) return;

    await deleteMatterFile.mutateAsync({
      matterId: matterItemToDelete.matterId,
      id: matterItemToDelete.item.id,
    });

    if (matterSelectedItemId === matterItemToDelete.item.id) {
      setMatterSelectedItemIdForMatter(matterItemToDelete.matterId, null);
    }

    setMatterItemToDelete(null);
  };

  const handleDeleteMassTortItem = async () => {
    if (!massTortItemToDelete) return;

    await deleteMatterFile.mutateAsync({
      massTortId: massTortItemToDelete.massTortId,
      id: massTortItemToDelete.item.id,
    });

    if (massTortSelectedItemId === massTortItemToDelete.item.id) {
      setMassTortSelectedItemIdForMassTort(
        massTortItemToDelete.massTortId,
        null,
      );
    }

    setMassTortItemToDelete(null);
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;

    await deleteMatterTemplate.mutateAsync({
      id: templateToDelete.id,
    });

    if (selectedTemplateId === templateToDelete.id) {
      setSelectedTemplateId(null);
    }

    if (templateDocxEditorTemplate?.id === templateToDelete.id) {
      setTemplateDocxEditorTemplate(null);
    }

    setTemplateEditorState((prev) =>
      prev.activeTemplate?.id === templateToDelete.id
        ? { open: false, editing: false, activeTemplate: undefined }
        : prev,
    );

    setTemplateToDelete(null);
  };

  const handleDownloadSelectedTemplate = async () => {
    if (!selectedTemplate) return;
    await downloadGlobalFile(selectedTemplate, "matter-template");
  };

  const handleDuplicateSelectedTemplate = () => {
    if (!selectedTemplate) return;
    setTemplateToDuplicate(selectedTemplate);
  };

  const canDownloadSelectedTemplate = Boolean(
    selectedTemplate?.presignedUrlKey,
  );
  const canDuplicateSelectedTemplate =
    canCreateTemplates && canDownloadSelectedTemplate;

  const renderRootFolderItem = ({
    section,
    title,
  }: {
    section: FilesSection;
    title: string;
  }) => {
    const isActive = activeSection === section;
    const isExpanded = expandedSections.has(section);

    return (
      <button
        type="button"
        onClick={() => {
          if (
            section === "personal" &&
            isExpanded &&
            isActive &&
            !personalFiles.atRoot
          ) {
            personalFiles.reset();
            return;
          }
          if (
            section === "firm" &&
            isExpanded &&
            isActive &&
            (firmCurrentFolderId !== "" || firmSelectedFileId !== null)
          ) {
            setFirmCurrentFolderId("");
            setFirmSelectedFileId(null);
            return;
          }

          if (isMatterFilesSection(section) && activeSection !== section) {
            finishMatterPreviewLoading();
            setSelectedMatterId(null);
          }

          if (section === "mass-torts" && activeSection !== section) {
            setSelectedMassTortId(null);
          }

          if (
            section === "mass-torts" &&
            isExpanded &&
            isActive &&
            selectedMassTortId &&
            (massTortCurrentFolderId !== "" || massTortSelectedItemId !== null)
          ) {
            setMassTortCurrentFolderIdForMassTort(selectedMassTortId, "");
            setMassTortSelectedItemIdForMassTort(selectedMassTortId, null);
            return;
          }

          toggleSection(section);
        }}
        className={cn(
          EXPLORER_ROW_CLASS,
          isActive && EXPLORER_ROW_SELECTED_CLASS,
        )}
        aria-expanded={isExpanded}
        title={title}
      >
        <ChevronRight
          className={cn(
            "mr-1 transition-transform",
            TREE_ICON_CLASS,
            isExpanded && "rotate-90",
          )}
        />
        <Folder
          className={cn("mr-2", TREE_ICON_CLASS, SYSTEM_FOLDER_ICON_CLASS)}
        />
        <Text
          variant="subHeading"
          color="foreground"
          as="span"
          className={EXPLORER_ROW_LABEL_CLASS}
        >
          {title}
        </Text>
      </button>
    );
  };

  const renderHeaderActions = () => {
    if (activeSection === null) {
      return null;
    }

    if (activeSection === "firm") {
      if (!canCreateFirmFiles) return null;
      return (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              openFirmCreate(true);
            }}
            type="button"
          >
            <Folder className="mr-2 h-4 w-4" />
            New folder
          </Button>
          <RepoUploadSplitButton
            onUploadFile={() => {
              openFirmCreate(false, { autoOpenUpload: true });
            }}
            options={[
              {
                label: "Create file",
                icon: FileText,
                onSelect: () => {
                  setIsCreateFirmDocxOpen(true);
                },
              },
              {
                label: "Bulk upload",
                icon: FilesIcon,
                onSelect: () => {
                  openMultiFilePicker(firmMultiFileInputRef);
                },
              },
            ]}
            loading={createFirmFile.isPending}
          />
        </div>
      );
    }

    if (activeSection === "templates") {
      if (!canCreateTemplates) return null;
      return (
        <div className="flex justify-end">
          <RepoUploadSplitButton
            onUploadFile={() => {
              openTemplateCreate({ autoOpenUpload: true });
            }}
            label="Upload template"
            buttonIcon={FileIcon}
            options={[
              {
                label: "Create template",
                icon: FileText,
                onSelect: () => {
                  setIsCreateTemplateDocxOpen(true);
                },
              },
              {
                label: "Bulk upload",
                icon: FilesIcon,
                onSelect: () => {
                  setIsBulkUploadTemplatesOpen(true);
                },
              },
            ]}
          />
        </div>
      );
    }

    if (activeSection === "mass-torts" && !isMassTortMattersActive) {
      if (!canCreateMatterFiles) return null;
      return (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              openMassTortCreate(true);
            }}
            type="button"
            disabled={!selectedMassTortId}
          >
            <Folder className="mr-2 h-4 w-4" />
            New folder
          </Button>
          <RepoUploadSplitButton
            onUploadFile={() => {
              openMassTortCreate(false, { autoOpenUpload: true });
            }}
            options={[
              {
                label: "Bulk upload",
                icon: FilesIcon,
                onSelect: () => {
                  openMultiFilePicker(massTortMultiFileInputRef);
                },
                disabled: !selectedMassTortId,
              },
            ]}
            loading={createMatterFile.isPending}
            disabled={!selectedMassTortId}
          />
        </div>
      );
    }

    if (!isMatterRepositoryActive) {
      return null;
    }

    return canCreateMatterFiles ? (
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            openMatterCreate(true);
          }}
          type="button"
          disabled={!selectedMatterId}
        >
          <Folder className="mr-2 h-4 w-4" />
          New folder
        </Button>
        <RepoUploadSplitButton
          onUploadFile={() => {
            openMatterCreate(false, { autoOpenUpload: true });
          }}
          options={[
            {
              label: "Create file",
              icon: FileText,
              onSelect: () => {
                setIsCreateMatterDocxOpen(true);
              },
              disabled: !selectedMatterId,
            },
            {
              label: "Bulk upload",
              icon: FilesIcon,
              onSelect: () => {
                openMultiFilePicker(matterMultiFileInputRef);
              },
              disabled: !selectedMatterId,
            },
          ]}
          loading={createMatterFile.isPending}
          disabled={!selectedMatterId}
        />
      </div>
    ) : null;
  };

  const renderExplorerToolbar = () => {
    if (activeSection === "personal") return personalFiles.toolbar;
    const actions = renderHeaderActions();
    return (
      <div className="shrink-0 border-b px-4 py-4">
        {actions ?? (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" type="button" disabled>
              <Folder className="mr-2 h-4 w-4" />
              New folder
            </Button>
            <RepoUploadSplitButton
              onUploadFile={() => {}}
              options={[]}
              disabled
            />
          </div>
        )}
      </div>
    );
  };

  const renderExplorer = () => {
    const renderMassTortRows = () =>
      massTortFolders.map((massTort) => {
        const isExpanded = expandedMassTortIds.has(massTort.massTortId);
        const isSelected =
          activeSection === "mass-torts" &&
          !isMassTortMattersActive &&
          selectedMassTortId === massTort.massTortId;
        const selectedItemIdForMassTort =
          massTortSelectedItemIds[massTort.massTortId] ?? null;
        const currentFolderIdForMassTort =
          massTortCurrentFolderIds[massTort.massTortId] ?? "";
        const isMassTortLoading =
          expandedMassTortLoadingMap.get(massTort.massTortId) ?? false;
        const expandedMassTort = expandedMassTortMap.get(massTort.massTortId);
        const massTortItems = isSelected
          ? selectedMassTortRepoItems
          : getMassTortRepoItems(expandedMassTort);

        return (
          <div key={massTort.massTortId}>
            <button
              type="button"
              onClick={() => {
                if (!isExpanded) {
                  selectMassTortFolder(massTort);
                  return;
                }

                if (!isSelected) {
                  setActiveSection("mass-torts");
                  setActiveMassTortMattersId(null);
                  setSelectedMassTortId(massTort.massTortId);
                  return;
                }

                if (
                  currentFolderIdForMassTort !== "" ||
                  selectedItemIdForMassTort !== null
                ) {
                  setActiveSection("mass-torts");
                  setActiveMassTortMattersId(null);
                  setSelectedMassTortId(massTort.massTortId);
                  setMassTortCurrentFolderIdForMassTort(
                    massTort.massTortId,
                    "",
                  );
                  setMassTortSelectedItemIdForMassTort(
                    massTort.massTortId,
                    null,
                  );
                  return;
                }

                collapseMassTortFolder(massTort.massTortId);
              }}
              className={cn(
                EXPLORER_ROW_CLASS,
                isSelected && EXPLORER_ROW_SELECTED_CLASS,
              )}
              aria-busy={isMassTortLoading}
              style={{ paddingLeft: "20px" }}
              title={massTort.massTortTitle}
            >
              <ChevronRight
                className={cn(
                  "mr-1 transition-transform",
                  TREE_ICON_CLASS,
                  isExpanded && "rotate-90",
                )}
              />
              <Folder
                className={cn(
                  "mr-2",
                  TREE_ICON_CLASS,
                  SYSTEM_FOLDER_ICON_CLASS,
                )}
              />
              <Text
                variant="subHeading"
                color="foreground"
                as="span"
                className={EXPLORER_ROW_LABEL_CLASS}
              >
                {massTort.massTortTitle}
              </Text>
            </button>

            {isExpanded ? (
              <div className="pl-5">
                {isMassTortLoading || !expandedMassTort ? (
                  <ExplorerInlineLoading label="Loading files..." />
                ) : massTortItems.length > 0 ? (
                  <FolderTree
                    matter={{
                      matterTitle: expandedMassTort.massTortTitle,
                    }}
                    items={massTortItems}
                    selectionItems={
                      isSelected
                        ? selectedMassTortRepoItems
                        : getMassTortRepoItems(expandedMassTort)
                    }
                    onSelectItem={(itemId) => {
                      setActiveSection("mass-torts");
                      setActiveMassTortMattersId(null);
                      setSelectedMassTortId(massTort.massTortId);
                      setMassTortSelectedItemIdForMassTort(
                        massTort.massTortId,
                        itemId,
                      );
                      if (!itemId) {
                        setMassTortCurrentFolderIdForMassTort(
                          massTort.massTortId,
                          "",
                        );
                        return;
                      }

                      const item = massTortItems.find(
                        (repoItem) => repoItem.id === itemId,
                      );
                      if (!item) return;

                      if (item.isFolder) {
                        setMassTortCurrentFolderIdForMassTort(
                          massTort.massTortId,
                          item.id,
                        );
                        return;
                      }

                      setMassTortCurrentFolderIdForMassTort(
                        massTort.massTortId,
                        item.idLocation || "",
                      );
                    }}
                    selectedItemId={isSelected ? massTortSelectedItemId : null}
                    onMoveItem={(item, targetFolderId) =>
                      moveMassTortItem(
                        item as MatterFile,
                        targetFolderId,
                        expandedMassTort,
                      )
                    }
                    currentFolderId={isSelected ? massTortCurrentFolderId : ""}
                    showHeader={false}
                    expandRootFoldersWhenHeaderHidden={false}
                    movingItemIds={massTortMovingItemIds}
                    onNavigateToFolder={(folderId) => {
                      setActiveSection("mass-torts");
                      setActiveMassTortMattersId(null);
                      setSelectedMassTortId(massTort.massTortId);
                      setMassTortCurrentFolderIdForMassTort(
                        massTort.massTortId,
                        folderId,
                      );
                    }}
                    renderContextMenu={(item, trigger) => (
                      <GlobalFileContextMenu
                        matterFileId={item.id}
                        item={item}
                        trigger={trigger}
                        canEdit={canUpdateMatterFiles}
                        canDelete={canDeleteMatterFiles}
                        canDuplicateFile={canCreateMatterFiles}
                        onDuplicateFile={() => {
                          openDuplication({
                            files: [
                              {
                                id: item.id,
                                name: item.name,
                                isFolder: !!item.isFolder,
                              },
                            ],
                            repository: {
                              scope: "mass_tort",
                              massTortId: massTort.massTortId,
                            },
                            label: item.name,
                          });
                        }}
                        onEdit={() => {
                          openMassTortEdit(item as MatterFile);
                        }}
                        onDownload={() =>
                          void downloadGlobalFile(item, "matter-file")
                        }
                        onDelete={() => {
                          setMassTortItemToDelete({
                            item: item as MatterFile,
                            massTortId: massTort.massTortId,
                          });
                        }}
                      />
                    )}
                  />
                ) : null}
                <MassTortMattersFolder
                  expanded={expandedMassTortMattersIds.has(massTort.massTortId)}
                  selected={
                    isMassTortMattersActive &&
                    activeMassTortMattersId === massTort.massTortId &&
                    !selectedMatterId
                  }
                  onSelect={() => {
                    const isGroupActive =
                      isMassTortMattersActive &&
                      activeMassTortMattersId === massTort.massTortId &&
                      !selectedMatterId;
                    setActiveSection("mass-torts");
                    setSelectedMassTortId(massTort.massTortId);
                    setActiveMassTortMattersId(massTort.massTortId);
                    setSelectedMatterId(null);
                    setExpandedMassTortMattersIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(massTort.massTortId) && isGroupActive)
                        next.delete(massTort.massTortId);
                      else next.add(massTort.massTortId);
                      return next;
                    });
                  }}
                >
                  {renderMassTortMatters(massTort.massTortId)}
                </MassTortMattersFolder>
              </div>
            ) : null}
          </div>
        );
      });

    const renderMassTortMatters = (massTortId: string) => {
      const queryIndex = expandedMassTortMattersIdList.indexOf(massTortId);
      const query =
        queryIndex < 0 ? undefined : massTortMattersQueries.at(queryIndex);
      if (!query) return null;
      if (!query.data && !query.isError)
        return <ExplorerInlineLoading label="Loading matters..." />;
      if (query.isError)
        return (
          <div className="p-3">
            <Text variant="subHeading">Unable to load matters.</Text>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void query.refetch()}
            >
              Retry
            </Button>
          </div>
        );
      const matters = matterFolders.filter(
        (matter) => matter.massTortId === massTortId,
      );
      return matters.length ? (
        renderMatterRows(matters, "mass-torts", "", massTortId)
      ) : (
        <div className="p-3">
          <Text variant="subHeading">No matters found.</Text>
        </div>
      );
    };

    const renderMatterRows = (
      matters: MatterFolderListItem[],
      section: MatterFilesSection | "mass-torts",
      nestingClassName = "",
      massTortId?: string,
    ) => (
      <VirtualMatterFolderList
        items={matters}
        expandedMatterIds={expandedMatterIds}
        scrollElement={explorerScrollElement}
        renderItem={(matter) => {
          const isExpanded = expandedMatterIds.has(matter.matterId);
          const isSelected =
            selectedMatterId === matter.matterId &&
            activeSection === section &&
            (section !== "mass-torts" ||
              activeMassTortMattersId === massTortId);
          const activateMatter = () => {
            setActiveSection(section);
            setActiveMassTortMattersId(massTortId ?? null);
            if (massTortId) setSelectedMassTortId(massTortId);
            setSelectedMatterId(matter.matterId);
          };
          const selectedItemIdForMatter =
            matterSelectedItemIds[matter.matterId] ?? null;
          const currentFolderIdForMatter =
            matterCurrentFolderIds[matter.matterId] ?? "";
          const isMatterLoading =
            expandedMatterLoadingMap.get(matter.matterId) ?? false;
          const expandedMatter = expandedMatterMap.get(matter.matterId);
          const matterItems = isSelected
            ? selectedMatterRepoItems
            : getMatterRepoItems(expandedMatter);

          return (
            <div key={matter.matterId} className={nestingClassName}>
              <div className="group flex items-center gap-1 pr-1">
                <button
                  type="button"
                  onClick={() => {
                    if (!isExpanded) {
                      selectMatterFolder(matter, massTortId);
                      return;
                    }

                    if (!isSelected) {
                      activateMatter();
                      return;
                    }

                    if (
                      currentFolderIdForMatter !== "" ||
                      selectedItemIdForMatter !== null
                    ) {
                      finishMatterPreviewLoading();
                      activateMatter();
                      setMatterCurrentFolderIdForMatter(matter.matterId, "");
                      setMatterSelectedItemIdForMatter(matter.matterId, null);
                      return;
                    }

                    collapseMatterFolder(matter.matterId, massTortId);
                  }}
                  className={cn(
                    EXPLORER_ROW_CLASS,
                    "min-w-0 flex-1",
                    isSelected && EXPLORER_ROW_SELECTED_CLASS,
                  )}
                  aria-busy={isMatterLoading}
                  style={{ paddingLeft: "20px" }}
                  title={matter.matterTitle || "Untitled matter"}
                >
                  <ChevronRight
                    className={cn(
                      "mr-1 transition-transform",
                      TREE_ICON_CLASS,
                      isExpanded && "rotate-90",
                    )}
                  />
                  <Folder
                    className={cn(
                      "mr-2",
                      TREE_ICON_CLASS,
                      SYSTEM_FOLDER_ICON_CLASS,
                    )}
                  />
                  <Text
                    variant="subHeading"
                    color="foreground"
                    as="span"
                    className={EXPLORER_ROW_LABEL_CLASS}
                  >
                    {matter.matterTitle || "Untitled matter"}
                  </Text>
                </button>
                <MatterPreviewHoverCard
                  align="start"
                  matterId={matter.matterId}
                  previewData={matter}
                  side="right"
                >
                  <a
                    href={`/matters/${matter.matterId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground focus-visible:ring-ring inline-flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors focus-visible:ring-2 focus-visible:outline-none"
                    title={`Open ${matter.matterTitle || "matter"}`}
                    aria-label={`Open ${matter.matterTitle || "matter"}`}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </MatterPreviewHoverCard>
              </div>

              {isExpanded ? (
                <div className="pl-5">
                  {isMatterLoading || !expandedMatter ? (
                    <ExplorerInlineLoading label="Loading files..." />
                  ) : (
                    <FolderTree
                      matter={{
                        matterTitle: expandedMatter.matterTitle,
                      }}
                      items={matterItems}
                      selectionItems={
                        isSelected
                          ? selectedMatterRepoItems
                          : getMatterRepoItems(expandedMatter)
                      }
                      onSelectItem={(itemId) => {
                        activateMatter();
                        setMatterSelectedItemIdForMatter(
                          matter.matterId,
                          itemId,
                        );
                        if (!itemId) {
                          finishMatterPreviewLoading();
                          setMatterCurrentFolderIdForMatter(
                            matter.matterId,
                            "",
                          );
                          return;
                        }

                        const item = matterItems.find(
                          (repoItem) => repoItem.id === itemId,
                        );
                        if (!item) return;

                        if (item.isFolder) {
                          finishMatterPreviewLoading();
                          setMatterCurrentFolderIdForMatter(
                            matter.matterId,
                            item.id,
                          );
                          return;
                        }

                        setMatterCurrentFolderIdForMatter(
                          matter.matterId,
                          item.idLocation || "",
                        );
                        if (isSelected && selectedItemIdForMatter === item.id) {
                          return;
                        }

                        if (item.presignedUrlKey) {
                          clearMatterPreviewLoadingTimeout();
                          matterPreviewLoadingStartedAtRef.current = Date.now();
                          setIsMatterPreviewLoading(true);
                          setMatterPreviewLoadingItemId(item.id);
                        } else {
                          finishMatterPreviewLoading();
                        }
                      }}
                      selectedItemId={isSelected ? matterSelectedItemId : null}
                      onMoveItem={(item, targetFolderId) =>
                        moveMatterItem(
                          item as MatterFile,
                          targetFolderId,
                          expandedMatter,
                        )
                      }
                      currentFolderId={isSelected ? matterCurrentFolderId : ""}
                      showHeader={false}
                      expandRootFoldersWhenHeaderHidden={false}
                      previewLoadingItemId={
                        isSelected && isMatterPreviewLoading
                          ? matterPreviewLoadingItemId
                          : null
                      }
                      movingItemIds={matterMovingItemIds}
                      onNavigateToFolder={(folderId) => {
                        activateMatter();
                        setMatterCurrentFolderIdForMatter(
                          matter.matterId,
                          folderId,
                        );
                      }}
                      renderContextMenu={(item, trigger) => (
                        <GlobalFileContextMenu
                          matterFileId={item.id}
                          item={item}
                          trigger={trigger}
                          canEdit={canUpdateMatterFiles}
                          canDelete={canDeleteMatterFiles}
                          canDuplicateFile={canCreateMatterFiles}
                          onDuplicateFile={() => {
                            openDuplication({
                              files: [
                                {
                                  id: item.id,
                                  name: item.name,
                                  isFolder: !!item.isFolder,
                                },
                              ],
                              repository: {
                                scope: "matter",
                                matterId: matter.matterId,
                              },
                              label: item.name,
                            });
                          }}
                          onEdit={() => {
                            activateMatter();
                            openMatterEdit(item as MatterFile);
                          }}
                          onDownload={() =>
                            void downloadGlobalFile(item, "matter-file")
                          }
                          onDelete={() => {
                            setMatterItemToDelete({
                              item: item as MatterFile,
                              matterId: matter.matterId,
                            });
                          }}
                        />
                      )}
                    />
                  )}
                </div>
              ) : null}
            </div>
          );
        }}
      />
    );

    const renderMatterSectionBody = (
      section: MatterFilesSection,
      matters: MatterFolderListItem[],
      emptyMessage: string,
    ) => (
      <div className="flex min-h-0 flex-col">
        <div className="min-h-0 flex-1 overflow-auto">
          {isMatterFoldersLoading ||
          (isMatterFoldersFetching && matterFolders.length === 0) ? (
            <ExplorerSkeleton rows={8} />
          ) : section === "matters" ? (
            openMatterFolders.length > 0 || closedMatterFolders.length > 0 ? (
              <div>
                {renderMatterRows(openMatterFolders, section)}
                {closedMatterFolders.length > 0 ? (
                  <div>
                    <button
                      type="button"
                      onClick={toggleClosedMatterFolder}
                      className={cn(
                        EXPLORER_ROW_CLASS,
                        isSelectedMatterClosed && EXPLORER_ROW_SELECTED_CLASS,
                      )}
                      style={{ paddingLeft: "20px" }}
                      title="Closed"
                    >
                      <ChevronRight
                        className={cn(
                          "mr-1 transition-transform",
                          TREE_ICON_CLASS,
                          isClosedMatterFolderExpanded && "rotate-90",
                        )}
                      />
                      <Folder
                        className={cn(
                          "mr-2",
                          TREE_ICON_CLASS,
                          SYSTEM_FOLDER_ICON_CLASS,
                        )}
                      />
                      <Text
                        variant="subHeading"
                        color="foreground"
                        as="span"
                        className={EXPLORER_ROW_LABEL_CLASS}
                      >
                        Closed
                      </Text>
                    </button>

                    {isClosedMatterFolderExpanded ? (
                      <div className="pl-5">
                        {renderMatterRows(closedMatterFolders, section)}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="p-3">
                <Text variant="subHeading">No matters found.</Text>
              </div>
            )
          ) : matters.length > 0 ? (
            <div>{renderMatterRows(matters, section)}</div>
          ) : (
            <div className="p-3">
              <Text variant="subHeading">{emptyMessage}</Text>
            </div>
          )}
        </div>
      </div>
    );

    const renderSectionBody = (section: FilesSection) => {
      if (section === "personal") return personalFiles.tree;
      if (section === "firm") {
        if (isFirmFilesLoading) {
          return <ExplorerSkeleton />;
        }

        return (
          <div className="min-h-0 overflow-auto">
            <FirmFolderTree
              files={firmFiles}
              currentFolderId={firmCurrentFolderId}
              onNavigateToFolder={(folderId) => {
                setActiveSection("firm");
                setFirmCurrentFolderId(folderId);
                setFirmSelectedFileId(null);
              }}
              showRoot={false}
              showFiles
              enableFileDrag={canUpdateFirmFiles}
              onOpenFile={(item) => {
                setActiveSection("firm");
                setFirmSelectedFileId(item.id);
                setFirmCurrentFolderId(item.idLocation || "");
              }}
              selectedFileId={firmSelectedFileId}
              onMoveItem={(item, targetFolderId) => {
                void moveFirmItem(item, targetFolderId);
              }}
              externalDropTypes={[GLOBAL_FILES_TEMPLATE_DRAG_TYPE]}
              onExternalDrop={(payload, targetFolderId) => {
                void moveTemplateFilesToFirm(payload, targetFolderId);
              }}
              movingItemIds={firmMovingItemIds}
              rootLabel="Firm files"
              renderContextMenu={(item, trigger) => (
                <GlobalFileContextMenu
                  item={item}
                  trigger={trigger}
                  canEdit={canUpdateFirmFiles}
                  canDelete={canDeleteFirmFiles}
                  canDuplicateFile={canCreateFirmFiles}
                  onDuplicateFile={() => {
                    openDuplication({
                      files: [
                        {
                          id: item.id,
                          name: item.name,
                          isFolder: item.isFolder,
                        },
                      ],
                      repository: { scope: "firm" },
                      label: item.name,
                    });
                  }}
                  onEdit={() => {
                    openFirmEdit(item);
                  }}
                  onDownload={() => void downloadGlobalFile(item, "firm-file")}
                  onDelete={() => {
                    setFirmItemToDelete(item);
                  }}
                />
              )}
            />
          </div>
        );
      }

      if (section === "templates") {
        if (isTemplatesPending) {
          return <ExplorerSkeleton rows={7} />;
        }

        return (
          <div
            ref={templateDropRef}
            className={cn(
              "min-h-0 overflow-auto",
              isOverTemplateDropZone &&
                "bg-primary/5 border-primary/20 rounded-md border",
            )}
          >
            {templateRepoItems.length > 0 ? (
              <FolderTree
                matter={{ matterTitle: "Templates" }}
                items={templateRepoItems}
                onSelectItem={(itemId) => {
                  setActiveSection("templates");
                  setSelectedTemplateId(itemId || null);
                }}
                selectedItemId={selectedTemplateId}
                onMoveItem={() => false}
                currentFolderId=""
                showHeader={false}
                enableDrag={canCreateFirmFiles}
                dragType={GLOBAL_FILES_TEMPLATE_DRAG_TYPE}
                expandRootFoldersWhenHeaderHidden={false}
                movingItemIds={templateMovingItemIds}
                renderContextMenu={(item, trigger) => {
                  const template = item as MatterTemplate;
                  const canDownload = Boolean(template.presignedUrlKey);

                  return (
                    <GlobalFileContextMenu
                      item={template}
                      trigger={trigger}
                      canEdit={canUpdateTemplates}
                      canDelete={canDeleteTemplates}
                      canDuplicate={canCreateTemplates && canDownload}
                      onEdit={() => {
                        openTemplateEdit(template);
                      }}
                      onDownload={() =>
                        void downloadGlobalFile(template, "matter-template")
                      }
                      onDuplicate={() => {
                        setTemplateToDuplicate(template);
                      }}
                      onDelete={() => {
                        setTemplateToDelete(template);
                      }}
                    />
                  );
                }}
              />
            ) : (
              <div className="p-3">
                <Text variant="subHeading">No templates found.</Text>
              </div>
            )}
          </div>
        );
      }

      if (section === "mass-torts") {
        return (
          <div className="flex min-h-0 flex-col">
            <div className="min-h-0 flex-1 overflow-auto">
              {isMassTortFoldersLoading ||
              (isMassTortFoldersFetching && massTortFolders.length === 0) ? (
                <ExplorerSkeleton rows={6} />
              ) : massTortFolders.length > 0 ? (
                <div>{renderMassTortRows()}</div>
              ) : (
                <div className="p-3">
                  <Text variant="subHeading">No mass torts found.</Text>
                </div>
              )}
            </div>
          </div>
        );
      }

      if (section === "matters") {
        return renderMatterSectionBody(
          section,
          openMatterFolders,
          "No matters found.",
        );
      }
      if (section === "intake") {
        return renderMatterSectionBody(
          section,
          intakeMatterFolders,
          "No intake files found.",
        );
      }

      if (section === "referred-matters") {
        return renderMatterSectionBody(
          section,
          referredMatterFolders,
          "No referred matters found.",
        );
      }

      return renderMatterSectionBody(
        section,
        declinedMatterFolders,
        "No declined matters found.",
      );
    };

    return (
      <div
        ref={setExplorerScrollElement}
        className="min-h-0 flex-1 overflow-auto px-2 py-2"
      >
        <div>
          {MY_FILES_UI_ENABLED ? (
            <div>
              {renderRootFolderItem({ section: "personal", title: "My files" })}
              {expandedSections.has("personal") ? personalFiles.tree : null}
            </div>
          ) : null}
          {canViewFirmFiles ? (
            <div>
              {renderRootFolderItem({
                section: "firm",
                title: "Firm files",
              })}
              {expandedSections.has("firm") ? (
                <div>{renderSectionBody("firm")}</div>
              ) : null}
            </div>
          ) : null}
          {canViewTemplates ? (
            <div>
              {renderRootFolderItem({
                section: "templates",
                title: "Templates",
              })}
              {expandedSections.has("templates") ? (
                <div>{renderSectionBody("templates")}</div>
              ) : null}
            </div>
          ) : null}
          {canViewMassTortFiles ? (
            <div>
              {renderRootFolderItem({
                section: "mass-torts",
                title: "Mass torts",
              })}
              {expandedSections.has("mass-torts") ? (
                <div>{renderSectionBody("mass-torts")}</div>
              ) : null}
            </div>
          ) : null}
          {canViewMatterFiles ? (
            <div>
              {renderRootFolderItem({
                section: "matters",
                title: "Matters",
              })}
              {expandedSections.has("matters") ? (
                <div>{renderSectionBody("matters")}</div>
              ) : null}
            </div>
          ) : null}
          {canViewMatterFiles ? (
            <div>
              {renderRootFolderItem({
                section: "intake",
                title: "Intake",
              })}
              {expandedSections.has("intake") ? (
                <div>{renderSectionBody("intake")}</div>
              ) : null}
            </div>
          ) : null}
          {canViewMatterFiles ? (
            <div>
              {renderRootFolderItem({
                section: "referred-matters",
                title: "Referred matters",
              })}
              {expandedSections.has("referred-matters") ? (
                <div>{renderSectionBody("referred-matters")}</div>
              ) : null}
            </div>
          ) : null}
          {canViewMatterFiles ? (
            <div>
              {renderRootFolderItem({
                section: "declined-matters",
                title: "Declined matters",
              })}
              {expandedSections.has("declined-matters") ? (
                <div>{renderSectionBody("declined-matters")}</div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderPreviewHeader = () => {
    if (activeSection === "personal") return personalFiles.previewHeader;
    if (activeSection === null) {
      return (
        <div className="border-border flex items-center justify-between gap-4 border-b px-6 py-4">
          <div className="flex min-w-0 items-center gap-2">
            <Folder
              className={cn("h-5 w-5 shrink-0", SYSTEM_FOLDER_ICON_CLASS)}
            />
            <Text
              variant="subHeading"
              color="foreground"
              className={PREVIEW_TITLE_CLASS}
              title="All files"
            >
              All files
            </Text>
          </div>
        </div>
      );
    }

    if (activeSection === "firm") {
      const currentName = firmSelectedItem
        ? getFirmFileDisplayName(firmSelectedItem)
        : "Firm files";
      const HeaderIcon = firmSelectedFile
        ? getFileIcon(getFirmFileDisplayName(firmSelectedFile))
        : Folder;

      return (
        <div className="border-border flex items-center justify-between gap-4 border-b px-6 py-4">
          <div className="flex min-w-0 items-center gap-2">
            <HeaderIcon
              className={cn(
                "h-5 w-5 shrink-0",
                firmSelectedFile ? "text-blue-500" : SYSTEM_FOLDER_ICON_CLASS,
              )}
            />
            <FileVersionTitle setContainer={setVersionControlsContainer}>
              <Text
                variant="subHeading"
                color="foreground"
                className={PREVIEW_TITLE_CLASS}
                title={currentName}
              >
                {currentName}
              </Text>
            </FileVersionTitle>

            {canUpdateFirmFiles && firmSelectedItem ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  openFirmEdit(firmSelectedItem);
                }}
                type="button"
                title="Edit"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
          <div className="flex items-center gap-1">
            {canCreateFirmFiles &&
            firmSelectedItem &&
            canOfferFileDuplication(firmSelectedItem) ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  openDuplication({
                    files: [
                      {
                        id: firmSelectedItem.id,
                        name: firmSelectedItem.name,
                        isFolder: firmSelectedItem.isFolder,
                      },
                    ],
                    repository: { scope: "firm" },
                    label: firmSelectedItem.name,
                  });
                }}
                type="button"
                title={`Duplicate ${firmSelectedItem.isFolder ? "folder" : "file"}`}
              >
                <CopyPlus className="h-4 w-4" />
              </Button>
            ) : null}
            {canDeleteFirmFiles && firmSelectedItem ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setFirmItemToDelete(firmSelectedItem);
                }}
                type="button"
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>
      );
    }

    if (activeSection === "templates") {
      const HeaderIcon = selectedTemplate
        ? getFileIcon(selectedTemplate.name)
        : Folder;
      const headerTitle = selectedTemplate
        ? selectedTemplate.name
        : "Templates";

      return (
        <div className="border-border flex items-center justify-between gap-4 border-b px-6 py-4">
          <div className="flex min-w-0 items-center gap-2">
            <HeaderIcon
              className={cn(
                "h-5 w-5 shrink-0",
                selectedTemplate ? "text-blue-500" : SYSTEM_FOLDER_ICON_CLASS,
              )}
            />
            <Text
              variant="subHeading"
              color="foreground"
              className={PREVIEW_TITLE_CLASS}
              title={headerTitle}
            >
              {headerTitle}
            </Text>
            {selectedTemplate && canUpdateTemplates ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  openTemplateEdit(selectedTemplate);
                }}
                type="button"
                title="Edit"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
          <div className="flex items-center gap-1">
            {selectedTemplate ? (
              <TooltipWrapper message="Download">
                <span className="inline-flex">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      void handleDownloadSelectedTemplate();
                    }}
                    type="button"
                    disabled={!canDownloadSelectedTemplate}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipWrapper>
            ) : null}
            {selectedTemplate && canCreateTemplates ? (
              <TooltipWrapper message="Duplicate">
                <span className="inline-flex">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleDuplicateSelectedTemplate}
                    type="button"
                    disabled={!canDuplicateSelectedTemplate}
                  >
                    <CopyPlus className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipWrapper>
            ) : null}
            {selectedTemplate && canDeleteTemplates ? (
              <TooltipWrapper message="Delete">
                <span className="inline-flex">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setTemplateToDelete(selectedTemplate);
                    }}
                    type="button"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipWrapper>
            ) : null}
          </div>
        </div>
      );
    }

    if (activeSection === "mass-torts" && !isMassTortMattersActive) {
      const massTortName =
        selectedMassTort?.massTortTitle ||
        selectedMassTortFolder?.massTortTitle ||
        "Mass torts";
      const selectedMassTortFolderItem = selectedMassTortItem?.isFolder
        ? selectedMassTortItem
        : selectedMassTort?.matterFile.find(
            (item) => item.id === massTortCurrentFolderId && item.isFolder,
          );
      const HeaderIcon = selectedMassTortFile
        ? getFileIcon(selectedMassTortFile.name)
        : Folder;
      const headerTitle =
        selectedMassTortFile?.name ||
        selectedMassTortFolderItem?.name ||
        massTortName;
      const editableMassTortItem =
        selectedMassTortFile ?? selectedMassTortFolderItem;

      return (
        <div className="border-border flex items-center justify-between gap-4 border-b px-6 py-4">
          <div className="flex min-w-0 items-center gap-2">
            <HeaderIcon
              className={cn(
                "h-5 w-5 shrink-0",
                selectedMassTortFile
                  ? "text-blue-500"
                  : selectedMassTortFolderItem
                    ? USER_FOLDER_ICON_CLASS
                    : SYSTEM_FOLDER_ICON_CLASS,
              )}
            />
            <FileVersionTitle setContainer={setVersionControlsContainer}>
              <Text
                variant="subHeading"
                color="foreground"
                className={PREVIEW_TITLE_CLASS}
                title={headerTitle}
              >
                {headerTitle}
              </Text>
            </FileVersionTitle>

            {editableMassTortItem && canUpdateMatterFiles ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  openMassTortEdit(editableMassTortItem);
                }}
                type="button"
                title="Edit"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
          <div className="flex items-center gap-1">
            {selectedMassTortItem &&
            selectedMassTortId &&
            canCreateMatterFiles &&
            canOfferFileDuplication(selectedMassTortItem) ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  openDuplication({
                    files: [
                      {
                        id: selectedMassTortItem.id,
                        name: selectedMassTortItem.name,
                        isFolder: !!selectedMassTortItem.isFolder,
                      },
                    ],
                    repository: {
                      scope: "mass_tort",
                      massTortId: selectedMassTortId,
                    },
                    label: selectedMassTortItem.name,
                  });
                }}
                type="button"
                title={`Duplicate ${selectedMassTortItem.isFolder ? "folder" : "file"}`}
              >
                <CopyPlus className="h-4 w-4" />
              </Button>
            ) : null}
            {selectedMassTortItem && canDeleteMatterFiles ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (!selectedMassTortId) return;
                  setMassTortItemToDelete({
                    item: selectedMassTortItem,
                    massTortId: selectedMassTortId,
                  });
                }}
                type="button"
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>
      );
    }

    if (isMassTortMattersActive && !selectedMatterId) {
      return (
        <div className="border-border flex items-center gap-2 border-b px-6 py-4">
          <FolderKanban
            className={cn("h-5 w-5 shrink-0", SYSTEM_FOLDER_ICON_CLASS)}
          />
          <Text variant="subHeading" color="foreground">
            Matters
          </Text>
        </div>
      );
    }

    if (!isMatterRepositoryActive) {
      return null;
    }

    const matterName =
      selectedMatter?.matterTitle ||
      selectedMatterFolder?.matterTitle ||
      activeMatterSectionTitle;
    const selectedMatterFolderItem = selectedMatterItem?.isFolder
      ? selectedMatterItem
      : selectedMatter?.matterFile.find(
          (item) => item.id === matterCurrentFolderId && item.isFolder,
        );
    const HeaderIcon = selectedMatterFile
      ? getFileIcon(selectedMatterFile.name)
      : Folder;
    const headerTitle =
      selectedMatterFile?.name || selectedMatterFolderItem?.name || matterName;
    const canOpenMatterFromHeader = Boolean(
      selectedMatterId && !selectedMatterFile,
    );
    const editableMatterItem = selectedMatterFile ?? selectedMatterFolderItem;

    return (
      <div className="border-border flex items-center justify-between gap-4 border-b px-6 py-4">
        <div className="flex min-w-0 items-center gap-2">
          <HeaderIcon
            className={cn(
              "h-5 w-5 shrink-0",
              selectedMatterFile
                ? "text-blue-500"
                : selectedMatterFolderItem
                  ? USER_FOLDER_ICON_CLASS
                  : SYSTEM_FOLDER_ICON_CLASS,
            )}
          />
          {canOpenMatterFromHeader ? (
            <MatterPreviewHoverCard
              matterId={selectedMatterId ?? undefined}
              previewData={selectedMatterFolder}
            >
              <Text
                as="a"
                href={`/matters/${selectedMatterId}`}
                target="_blank"
                rel="noopener noreferrer"
                variant="subHeading"
                color="foreground"
                className={cn(
                  PREVIEW_TITLE_CLASS,
                  "hover:text-primary hover:underline",
                )}
                title={headerTitle}
              >
                {headerTitle}
              </Text>
            </MatterPreviewHoverCard>
          ) : (
            <FileVersionTitle setContainer={setVersionControlsContainer}>
              <Text
                variant="subHeading"
                color="foreground"
                className={PREVIEW_TITLE_CLASS}
                title={headerTitle}
              >
                {headerTitle}
              </Text>
            </FileVersionTitle>
          )}

          {editableMatterItem && canUpdateMatterFiles ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                openMatterEdit(editableMatterItem);
              }}
              type="button"
              title="Edit"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          {selectedMatterItem &&
          selectedMatterId &&
          canCreateMatterFiles &&
          canOfferFileDuplication(selectedMatterItem) ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                openDuplication({
                  files: [
                    {
                      id: selectedMatterItem.id,
                      name: selectedMatterItem.name,
                      isFolder: !!selectedMatterItem.isFolder,
                    },
                  ],
                  repository: { scope: "matter", matterId: selectedMatterId },
                  label: selectedMatterItem.name,
                });
              }}
              type="button"
              title={`Duplicate ${selectedMatterItem.isFolder ? "folder" : "file"}`}
            >
              <CopyPlus className="h-4 w-4" />
            </Button>
          ) : null}
          {selectedMatterItem && canDeleteMatterFiles ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                if (!selectedMatterId) return;
                setMatterItemToDelete({
                  item: selectedMatterItem,
                  matterId: selectedMatterId,
                });
              }}
              type="button"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>
    );
  };

  const renderPreviewBody = () => {
    if (activeSection === "personal") return personalFiles.previewBody;
    if (activeSection === null) {
      return (
        <EmptyPreviewState
          title="All files"
          description="Select a folder."
          icon={Folder}
        />
      );
    }

    if (activeSection === "firm") {
      if (isFirmFilesLoading) {
        return <PreviewSkeleton />;
      }

      if (firmSelectedFile && canDownloadFile(firmSelectedFile)) {
        return (
          <div className="flex h-full min-h-0 flex-col overflow-hidden p-6">
            <FileViewer
              versionControlsContainer={versionControlsContainer}
              file={{
                fileName: getFirmFileDisplayName(firmSelectedFile),
                fileId: firmSelectedFile.id,
                presignedURLKey: firmSelectedFile.presignedUrlKey || "",
              }}
              fileType="firm-file"
              showDownload={false}
              showTitle={false}
              footerClassName="hidden"
              viewerClassName="bg-card min-h-0 items-stretch justify-stretch"
              wrapperClassName="bg-card min-h-0 rounded-none shadow-none"
              compactDocxPreview
              showDocxModeToggle={isSelectedFirmDocx}
              canEditDocx={canEditSelectedFirmDocx}
              onDocxEdit={openFirmDocxEditor}
              showEditOverlay={isSelectedFirmPdf}
              canEditOverlay={canEditSelectedFirmPdf}
              onEdit={canEditSelectedFirmPdf ? openFirmPdfEditor : undefined}
            />
          </div>
        );
      }

      if (firmCurrentFolder || !firmSelectedItem) {
        const firmPreviewFolderId = firmCurrentFolder?.id ?? "";
        const firmFolderDirectChildren = firmFiles.filter(
          (item) => (item.idLocation || "") === firmPreviewFolderId,
        );
        const fileCount = firmCurrentFolder
          ? countFirmDescendantFiles(firmFiles, firmCurrentFolder.id)
          : firmFiles.filter((item) => !item.isFolder).length;
        const firmFolderPreviewDescription =
          firmFolderDirectChildren.length === 0
            ? "This folder is empty"
            : `${fileCount} file${fileCount === 1 ? "" : "s"}`;

        return (
          <EmptyPreviewState
            title={firmCurrentFolder?.name || "Firm files"}
            description={firmFolderPreviewDescription}
            icon={firmCurrentFolder ? FolderOpen : Folder}
          />
        );
      }
    }

    if (activeSection === "templates") {
      if (isTemplatesPending) {
        return <PreviewSkeleton />;
      }

      if (!selectedTemplate?.presignedUrlKey) {
        return (
          <EmptyPreviewState title="Templates" description="" icon={Folder} />
        );
      }

      return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden p-6">
          <FileViewer
            versionControlsContainer={versionControlsContainer}
            file={{
              fileName: selectedTemplate.name,
              presignedURLKey: selectedTemplate.presignedUrlKey,
            }}
            fileType="matter-template"
            showDownload={false}
            showTitle={false}
            footerClassName="hidden"
            viewerClassName="bg-card min-h-0 items-stretch justify-stretch"
            wrapperClassName="bg-card min-h-0 rounded-none shadow-none"
            compactDocxPreview
            showDocxModeToggle={isSelectedTemplateDocx}
            canEditDocx={canEditSelectedTemplateDocx}
            onDocxEdit={openTemplateDocxEditor}
          />
        </div>
      );
    }

    if (activeSection === "mass-torts" && !isMassTortMattersActive) {
      if (!selectedMassTortId) {
        return (
          <EmptyPreviewState title="Mass torts" description="" icon={Folder} />
        );
      }

      if (isSelectedMassTortLoading || !selectedMassTort) {
        return (
          <EmptyPreviewState
            title={selectedMassTortFolder?.massTortTitle || "Mass torts"}
            description=""
            icon={Folder}
          />
        );
      }

      if (selectedMassTortFile && canDownloadFile(selectedMassTortFile)) {
        return (
          <div className="flex h-full min-h-0 flex-col overflow-hidden p-6">
            <FileViewer
              versionControlsContainer={versionControlsContainer}
              key={selectedMassTortFile.id}
              file={{
                fileName: selectedMassTortFile.name,
                fileId: selectedMassTortFile.id,
                presignedURLKey: selectedMassTortFile.presignedUrlKey || "",
              }}
              fileType="matter-file"
              showDownload={false}
              showTitle={false}
              footerClassName="hidden"
              viewerClassName="bg-card min-h-0 items-stretch justify-stretch"
              wrapperClassName="bg-card min-h-0 rounded-none shadow-none"
              compactDocxPreview
            />
          </div>
        );
      }

      const folder = selectedMassTortItem?.isFolder
        ? selectedMassTortItem
        : selectedMassTort.matterFile.find(
            (item) => item.id === massTortCurrentFolderId && item.isFolder,
          );
      const previewFolderId = massTortCurrentFolderId;
      const massTortFolderDirectChildren = selectedMassTortRepoItems.filter(
        (item) => (item.idLocation || "") === previewFolderId,
      );
      const massTortFolderDescendantFileCount = countMatterDescendantFiles(
        selectedMassTortRepoItems,
        previewFolderId,
      );
      const massTortFolderPreviewDescription =
        !previewFolderId && massTortFolderDirectChildren.length === 0
          ? "Open matters to browse matter files."
          : massTortFolderDirectChildren.length === 0
            ? "This folder is empty"
            : `${massTortFolderDescendantFileCount} file${massTortFolderDescendantFileCount === 1 ? "" : "s"}`;

      return (
        <div className="space-y-4 p-6">
          <EmptyPreviewState
            title={folder?.name || selectedMassTort.massTortTitle}
            description={massTortFolderPreviewDescription}
            icon={folder ? FolderOpen : Folder}
          />
        </div>
      );
    }

    if (isMassTortMattersActive && !selectedMatterId) {
      return (
        <EmptyPreviewState
          title="Matters"
          description="Select a matter folder."
          icon={FolderKanban}
        />
      );
    }

    if (!isMatterRepositoryActive) {
      return null;
    }

    if (!selectedMatterId) {
      return (
        <EmptyPreviewState
          title={activeMatterSectionTitle}
          description=""
          icon={Folder}
        />
      );
    }

    if (isSelectedMatterLoading || !selectedMatter) {
      return (
        <EmptyPreviewState
          title={selectedMatterFolder?.matterTitle || activeMatterSectionTitle}
          description=""
          icon={Folder}
        />
      );
    }

    if (selectedMatterFile && canDownloadFile(selectedMatterFile)) {
      return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden p-6">
          <FileViewer
            versionControlsContainer={versionControlsContainer}
            key={selectedMatterFile.id}
            file={{
              fileName: selectedMatterFile.name,
              fileId: selectedMatterFile.id,
              presignedURLKey: selectedMatterFile.presignedUrlKey || "",
            }}
            fileType="matter-file"
            showDownload={false}
            showTitle={false}
            footerClassName="hidden"
            viewerClassName="bg-card min-h-0 items-stretch justify-stretch"
            wrapperClassName="bg-card min-h-0 rounded-none shadow-none"
            compactDocxPreview
            showDocxModeToggle={isSelectedMatterDocx}
            canEditDocx={canEditSelectedMatterDocx}
            onLoadingStateChange={handleMatterPreviewLoadingChange}
            onDocxEdit={openMatterDocxEditor}
            showEditOverlay={isSelectedMatterPdf}
            canEditOverlay={canEditSelectedMatterPdf}
            onEdit={canEditSelectedMatterPdf ? openMatterPdfEditor : undefined}
          />
        </div>
      );
    }

    const folder = selectedMatterItem?.isFolder
      ? selectedMatterItem
      : selectedMatter.matterFile.find(
          (item) => item.id === matterCurrentFolderId && item.isFolder,
        );
    const previewFolderId = matterCurrentFolderId;
    const matterFolderDirectChildren = selectedMatterRepoItems.filter(
      (item) => (item.idLocation || "") === previewFolderId,
    );
    const matterFolderDescendantFileCount = countMatterDescendantFiles(
      selectedMatterRepoItems,
      previewFolderId,
    );
    const matterFolderPreviewDescription =
      matterFolderDirectChildren.length === 0
        ? "This folder is empty"
        : `${matterFolderDescendantFileCount} file${matterFolderDescendantFileCount === 1 ? "" : "s"}`;

    return (
      <div className="space-y-4 p-6">
        <EmptyPreviewState
          title={folder?.name || selectedMatter.matterTitle}
          description={matterFolderPreviewDescription}
          icon={folder ? FolderOpen : Folder}
        />
      </div>
    );
  };

  const hasAnyVisibleSection =
    canViewFirmFiles ||
    canViewTemplates ||
    canViewMassTortFiles ||
    canViewMatterFiles;

  if (!hasAnyVisibleSection) {
    return null;
  }

  return (
    <AppPageShell padding="none" className="h-full min-h-0 overflow-hidden">
      <>
        {personalFiles.dialogs}
        <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
          <div className="border-b px-4 py-3">
            <Input
              className="max-w-lg"
              aria-label="Search all files"
              placeholder="Search all files..."
              maxLength={200}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
          </div>
          <Card className="bg-card relative flex min-h-0 w-full flex-1 overflow-hidden rounded-none border-0 shadow-none">
            {isSearching ? (
              <GlobalFileSearchPanel query={search} />
            ) : (
              <>
                <div className="border-border bg-card flex h-full min-h-0 w-[360px] max-w-[360px] min-w-[360px] shrink-0 flex-col overflow-hidden border-r pt-14 md:pt-0 xl:w-[420px] xl:max-w-[420px] xl:min-w-[420px]">
                  {renderExplorerToolbar()}
                  {renderExplorer()}
                </div>

                <div className="bg-card relative flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                  {renderPreviewHeader()}
                  <div className="min-h-0 flex-1 overflow-auto">
                    {renderPreviewBody()}
                  </div>
                </div>
              </>
            )}
          </Card>
        </div>

        <input
          type="file"
          ref={firmMultiFileInputRef}
          className="hidden"
          multiple
          onChange={(event) => {
            handleFirmMultiFileUpload(event.target.files);
          }}
        />

        <input
          type="file"
          ref={matterMultiFileInputRef}
          className="hidden"
          multiple
          onChange={(event) => {
            handleMatterMultiFileUpload(event.target.files);
          }}
        />

        <input
          type="file"
          ref={massTortMultiFileInputRef}
          className="hidden"
          multiple
          onChange={(event) => {
            handleMassTortMultiFileUpload(event.target.files);
          }}
        />

        <AddOrEditFirmFileModal
          open={firmEditorState.open}
          setOpen={(open) => {
            setFirmEditorState((prev) => ({ ...prev, open }));
          }}
          editing={firmEditorState.editing}
          activeFile={firmEditorState.activeFile}
          currentFolderId={firmCurrentFolderId}
          isFolder={firmEditorState.isFolder}
          autoOpenUpload={firmEditorState.autoOpenUpload}
          existingFiles={firmFiles.filter(
            (item) =>
              (item.idLocation || "") ===
              (firmEditorState.editing
                ? firmEditorState.activeFile?.idLocation || ""
                : firmCurrentFolderId),
          )}
        />

        <CreateDocxFirmFileModal
          open={isCreateFirmDocxOpen}
          setOpen={setIsCreateFirmDocxOpen}
          currentFolderId={firmCurrentFolderId}
          existingFileNames={getFirmFileDisplayNamesInFolder(
            firmFiles,
            firmCurrentFolderId,
          )}
        />

        {firmDocxEditorFile ? (
          <EditDocxFirmFileModal
            open={!!firmDocxEditorFile}
            setOpen={(open) => {
              if (!open) {
                setFirmDocxEditorFile(null);
              }
            }}
            sourceFile={firmDocxEditorFile}
          />
        ) : null}

        {firmPdfEditorFile ? (
          <EditPdfFirmFileModal
            open={!!firmPdfEditorFile}
            setOpen={(open) => {
              if (!open) {
                setFirmPdfEditorFile(null);
              }
            }}
            sourceFile={firmPdfEditorFile}
          />
        ) : null}

        {templateEditorState.editing ? (
          <AddOrEditTemplateModal
            open={templateEditorState.open}
            setOpen={(open) => {
              setTemplateEditorState((prev) => ({ ...prev, open }));
            }}
            editing
            activeMatterTemplate={templateEditorState.activeTemplate}
            setActiveMatterTemplateId={() => {
              // The global files page manages selection locally and doesn't need the table state callback.
            }}
          />
        ) : (
          <AddOrEditTemplateModal
            open={templateEditorState.open}
            setOpen={(open) => {
              setTemplateEditorState((prev) => ({ ...prev, open }));
            }}
            editing={false}
            autoOpenUpload={templateEditorState.autoOpenUpload}
          />
        )}

        <BulkUploadMatterTemplatesModal
          open={isBulkUploadTemplatesOpen}
          setOpen={setIsBulkUploadTemplatesOpen}
        />

        <CreateDocxSlideover
          open={isCreateTemplateDocxOpen}
          setOpen={setIsCreateTemplateDocxOpen}
          title="Create template"
          description="Create a new template with fields."
          fileType="matter-template"
          existingFileNames={templates.map((template) => template.name)}
          onSave={handleCreateDocxTemplate}
        />

        {templateDocxEditorTemplate ? (
          <EditDocxTemplateModal
            open={!!templateDocxEditorTemplate}
            setOpen={(open) => {
              if (!open) {
                setTemplateDocxEditorTemplate(null);
              }
            }}
            template={templateDocxEditorTemplate}
          />
        ) : null}

        {templateToDuplicate ? (
          <DuplicateTemplateModal
            open={!!templateToDuplicate}
            setOpen={(open) => {
              if (!open) {
                setTemplateToDuplicate(null);
              }
            }}
            sourceTemplate={templateToDuplicate}
            existingTemplateNames={templates.map((template) => template.name)}
            onDuplicated={(template) => {
              setSelectedTemplateId(template.id);
            }}
          />
        ) : null}

        {selectedMatterId && isCreateMatterDocxOpen ? (
          <CreateDocxFileSlideover
            open={isCreateMatterDocxOpen}
            setOpen={setIsCreateMatterDocxOpen}
            matterId={selectedMatterId}
            currentFolderId={matterCurrentFolderId}
          />
        ) : null}

        {selectedMatterId && matterEditorState.open ? (
          <AddOrEditFileModal
            open={matterEditorState.open}
            setOpen={(open) => {
              setMatterEditorState((prev) => ({ ...prev, open }));
            }}
            editing={matterEditorState.editing}
            activeFileId={matterEditorState.activeFileId}
            matterId={selectedMatterId}
            currentFolderId={matterCurrentFolderId}
            isFolder={matterEditorState.isFolder}
            autoOpenUpload={matterEditorState.autoOpenUpload}
          />
        ) : null}

        {selectedMassTortId && massTortEditorState.open ? (
          <AddOrEditFileModal
            open={massTortEditorState.open}
            setOpen={(open) => {
              setMassTortEditorState((prev) => ({ ...prev, open }));
            }}
            editing={massTortEditorState.editing}
            activeFileId={massTortEditorState.activeFileId}
            massTortId={selectedMassTortId}
            currentFolderId={massTortCurrentFolderId}
            isFolder={massTortEditorState.isFolder}
            autoOpenUpload={massTortEditorState.autoOpenUpload}
          />
        ) : null}

        {selectedMatterId && matterDocxEditorFile ? (
          <EditDocxFileSlideover
            open={!!matterDocxEditorFile}
            setOpen={(open) => {
              if (!open) {
                setMatterDocxEditorFile(null);
              }
            }}
            matterId={selectedMatterId}
            sourceFile={matterDocxEditorFile}
          />
        ) : null}

        {selectedMatterId && matterPdfEditorFile ? (
          <FileRepositoryPdfEditorSlideover
            open={!!matterPdfEditorFile}
            setOpen={(open) => {
              if (!open) {
                setMatterPdfEditorFile(null);
              }
            }}
            matterId={selectedMatterId}
            sourceFile={matterPdfEditorFile}
          />
        ) : null}

        <ActionConfirmationModal
          open={Boolean(firmItemToDelete)}
          setOpen={(open) => {
            if (!open) {
              setFirmItemToDelete(undefined);
            }
          }}
          title="Delete firm item"
          description={`Delete ${firmItemToDelete ? getFirmFileDisplayName(firmItemToDelete) : "this item"}?`}
          buttonText="Delete"
          afterActionFn={() => {
            void handleDeleteFirmItem();
          }}
          actionVariant="destructive"
          isActionPending={deleteFirmFile.isPending}
        />

        <ActionConfirmationModal
          open={Boolean(templateToDelete)}
          setOpen={(open) => {
            if (!open) {
              setTemplateToDelete(null);
            }
          }}
          title="Delete template"
          description={
            templateToDelete
              ? `Delete ${templateToDelete.name}?`
              : "Delete this template?"
          }
          buttonText="Delete"
          afterActionFn={() => {
            void handleDeleteTemplate();
          }}
          actionVariant="destructive"
          isActionPending={deleteMatterTemplate.isPending}
        />

        <ActionConfirmationModal
          open={Boolean(matterItemToDelete)}
          setOpen={(open) => {
            if (!open) {
              setMatterItemToDelete(null);
            }
          }}
          title={`Delete ${matterItemToDelete?.item.isFolder ? "folder" : "file"}`}
          description={
            matterItemToDelete?.item.isFolder
              ? "Are you sure you want to delete this folder? If this folder has contents, they will be deleted as well. This action cannot be undone."
              : "Are you sure you want to delete this file? This action cannot be undone."
          }
          buttonText="Delete"
          afterActionFn={() => {
            void handleDeleteMatterItem();
          }}
          actionVariant="destructive"
          isActionPending={deleteMatterFile.isPending}
        />

        <ActionConfirmationModal
          open={Boolean(massTortItemToDelete)}
          setOpen={(open) => {
            if (!open) {
              setMassTortItemToDelete(null);
            }
          }}
          title={`Delete ${massTortItemToDelete?.item.isFolder ? "folder" : "file"}`}
          description={
            massTortItemToDelete?.item.isFolder
              ? "Are you sure you want to delete this folder? If this folder has contents, they will be deleted as well. This action cannot be undone."
              : "Are you sure you want to delete this file? This action cannot be undone."
          }
          buttonText="Delete"
          afterActionFn={() => {
            void handleDeleteMassTortItem();
          }}
          actionVariant="destructive"
          isActionPending={deleteMatterFile.isPending}
        />
      </>
    </AppPageShell>
  );
}
